import { RevealSensitiveButton } from '../components/RevealSensitiveButton'
import { useState, useEffect, useCallback, useRef, Fragment } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  CONTRACT_READINESS_COLOR_LABELS,
  CONTRACT_READINESS_COLOR_VARIANTS,
  CONTRACT_STATUS_COLORS,
  CONTRACT_STATUS_LABELS,
  CUSTOMER_SEGMENT_OPTIONS,
  PAYMENT_STATUS_OPTIONS,
  contractsApi,
} from '../api/contracts'
import type {
  Contract,
  ContractNote,
  ContractStatusHistoryEntry,
  ContractAttribution,
  ContractReadiness,
  PortalPhase,
} from '../api/contracts'
import { userDirectoryApi } from '../api/userDirectory'
import type { DirectoryUser } from '../api/userDirectory'
import {
  fetchCommissionPreview,
  fetchCommissionRuns,
  fetchCommissionRunDetail,
  createCommissionRun,
} from '../api/commissions'
import type {
  CommissionPreview,
  CommissionRunSummary,
  CommissionRunDetail,
  CommissionRunCreateResult,
} from '../api/commissions'
import { ContractProductRow } from '../components/ContractProductRow'
import { fetchMe } from '../api/auth'
import type { LoginResponse } from '../api/auth'
import { ContractTimeline } from '../components/ContractTimeline'
import { ContractGeneratedDocuments } from '../components/ContractGeneratedDocuments'
import { ContractErmesModules } from '../components/ContractErmesModules'
import { CONTRACT_DOCUMENT_TYPES, contractDocumentTypeLabel, documentsApi, type DocumentSummary } from '../api/documents'
import { FileDropZone } from '../components/FileDropZone'
import { ContractDynamicFields } from '../components/ContractDynamicFields'
import { ContractConfigurationSnapshots } from '../components/ContractConfigurationSnapshots'
import { ContractCallCenterData } from '../components/ContractCallCenterData'
import { ContractQueueStatus } from '../components/ContractQueueStatus'
import { TrashBanner } from '../components/TrashControls'
import { PrintShareButtons } from '../components/PrintShareButtons'
import { ContractLeadOrigin } from '../components/ContractLeadOrigin'
import { ContractCoralyPanel } from '../components/ContractCoralyPanel'
import { ContractOptionsPanel } from '../components/ContractOptionsPanel'
import { MandantePaymentsPanel } from '../components/MandantePaymentsPanel'
import { CustomerLinkPanel } from '../components/CustomerLinkPanel'
import { ContractSignaturePanel } from '../components/ContractSignaturePanel'
import { CustomerSignatureCard } from '../components/CustomerSignatureCard'
import { api, ApiError } from '../api/client'
import { customersApi } from '../api/customers'
import { customerFromDocs, docIdentity } from '../lib/customerMatch'
import { createOrLinkCustomer } from '../lib/customerLink'
import type { Customer } from '../api/customers'
import { fetchOperators, fetchDepartments, type Operator, type Department, type Product } from '../api/catalog'
import { fetchSelectableProducts } from '../lib/productSelection'
import { contractReferenceLabel, isTechnicalReference } from '../lib/contractColumns'
import { LINKED_PRACTICES_KEY } from '../lib/simLines'
import { operatorPortalConfig } from '../lib/operatorPortalConfig'
import { coralyUploadFromCrm } from '../lib/portalUploadSteps'
import { ContractPortalDataCard } from '../components/ContractPortalDataCard'
import { PortalSyncButton } from '../components/PortalSyncButton'
import { PortalIssuePanel } from '../components/PortalIssuePanel'
import { syncGestoreFor } from '../api/syncPortali'
import { ContractUnoMobileActivation } from '../components/ContractUnoMobileActivation'
import { ContractSection } from '../components/ContractSection'
import { ContractProgress } from '../components/ContractProgress'
import '../styles/contract-page.css'
import {
  Button, Callout, Card, CardContent, CardHeader, Checkbox, DataTable, Dialog, EmptyState, FormGrid, IconButton, KeyValueList, List, ListItem, OverflowMenu,
  LoadingState, Page, PageHeader, Select, StatusPill, TextField, type PillTone,
  useConfirm, usePrompt,
} from '../components/ui'
import { submitBlockedFrom, submitToBackoffice, type SubmissionFinding } from '../api/dynamicFields'

function formatDate(s: string): string {
  return new Date(s).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function formatDateTime(s: string): string {
  return new Date(s).toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

/**
 * TASK-0162: null-safe, exactly like `ContractsPage.tsx`'s `fmtEur` already was. This used to take a
 * non-optional `number` and was called with `c.baseAmount`/`c.addonTotal` straight from the API —
 * which was safe only for as long as `GET /api/contracts/{id}` returned every economic field to
 * every caller. It no longer does (REQ-301-006): for a caller whose category has
 * `CanSeeRealAmounts=false` those keys are genuinely ABSENT from the response, so the argument
 * arrives `undefined` and `undefined.toLocaleString()` would throw during render — killing the whole
 * page, not just the amount. Same null-vs-undefined class of bug TASK-0161 shipped on
 * `ContestDetailPage.tsx` and had to correct in review; fixed here in the same task that creates the
 * hazard, not deferred.
 *
 * `== null` (loose) is deliberate and matches `fmtEur`: it catches `null` as well as `undefined`,
 * and `agentVisibleAmount` is legitimately `null` when no agent-facing amount was ever recorded.
 */
function formatEur(n: number | null | undefined): string {
  if (n == null) return '—'
  return '€ ' + n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/** Colore bootstrap (api/contracts.ts) -> tono della StatusPill. */
function pillTone(color: string | undefined): PillTone {
  switch (color) {
    case 'success': return 'success'
    case 'danger': return 'error'
    case 'warning': return 'warning'
    case 'info': return 'info'
    case 'primary': return 'primary'
    default: return 'neutral'
  }
}

function statusBadge(status: string) {
  const color = CONTRACT_STATUS_COLORS[status] ?? 'secondary'
  const label = CONTRACT_STATUS_LABELS[status] ?? status
  return <StatusPill tone={pillTone(color)} className="me-2">{label}</StatusPill>
}

/**
 * TASK-0082: this map used to be keyed on `not_collected`/`partial`/`full` — the same dead
 * vocabulary TASK-0064 already found and removed from `ContractsPage.tsx`'s "Incasso" filter, where
 * only `partial` ever matched anything. `Contract.PaymentStatus` actually stores
 * `unpaid`/`partial`/`paid` (seeded `"unpaid"` by `Contract.Create`), so every real contract fell
 * through to the fallback below and rendered a raw, untranslated `"Unpaid"`/`"Paid"` badge. Rekeyed
 * onto the real vocabulary here — necessary for this task, whose whole point is a form that WRITES
 * these values: selecting "Incassato" and then seeing the badge say "Paid" would be an obviously
 * broken result. The two legacy keys are kept as aliases rather than deleted, so any pre-migration
 * row still carrying them keeps its Italian label instead of regressing to the fallback.
 */
function paymentStatusBadge(ps: string) {
  const map: Record<string, { tone: PillTone; label: string }> = {
    unpaid: { tone: 'neutral', label: 'Non incassato' },
    partial: { tone: 'warning', label: 'Parziale' },
    paid: { tone: 'success', label: 'Incassato' },
    // Legacy aliases — see the comment above.
    not_collected: { tone: 'neutral', label: 'Non incassato' },
    full: { tone: 'success', label: 'Incassato' },
  }
  const entry = map[ps] ?? {
    tone: 'neutral' as PillTone,
    label: ps.charAt(0).toUpperCase() + ps.slice(1),
  }
  return <StatusPill tone={entry.tone}>{entry.label}</StatusPill>
}

/** TASK-0267 (F-013): `CommissionPreview.base.source`'s values, labelled so the panel always
 * DECLARES where a base amount came from (this card's own additional requirement) rather than
 * showing a bare number. */
const COMMISSION_BASE_SOURCE_LABELS: Record<string, string> = {
  frozen: 'Congelata sul contratto',
  product_rule: 'Regola prodotto (F-004)',
  // TASK-0267 review correction: a real, reachable state (a product with a large negative
  // additive ProductRule) — never silently handed to the riparto, see the note alongside it.
  product_rule_negative: 'Regola prodotto — importo negativo, non utilizzabile',
  bracket_not_computable: 'Scaglione — non calcolabile automaticamente',
  not_determinable: 'Non determinabile',
  product_rule_error: 'Regola prodotto — errore',
  // Mirrors chain.truncation's own 'missing_signed_at' label below — same underlying cause.
  signed_at_mancante: 'Data di firma mancante',
}

/** `CommissionPreview.chain.truncation`'s full vocabulary — `CommissionChainResolver`'s own five
 * values plus the two this endpoint adds on top (`no_agent`/`no_department`). `'none'` has no
 * entry here on purpose: it renders nothing (see the panel below), not a "tutto ok" pill nobody
 * asked for. */
const COMMISSION_TRUNCATION_LABELS: Record<string, string> = {
  // TASK-0267 review correction: this module's own consolidated convention for a diagnostic GET —
  // an explicit outcome inside a 200, curated here into a real sentence instead of the raw
  // backend message that used to sit in a separate error alert.
  missing_signed_at: 'Il contratto non ha una data di firma: l\'anteprima del riparto richiede la data esatta e non usa alcuna data alternativa (creazione, chiusura prevista, oggi) come ripiego.',
  no_agent: 'Il contratto non ha un agente (venditore): nessuna catena da risolvere.',
  no_department: 'Il contratto non ha un settore (dipartimento): nessuna catena da risolvere.',
  no_assignment_for_seller: 'Nessuna assegnazione provvigionale per il venditore su questo settore e data.',
  manager_without_assignment_in_department: 'Un manager della catena non ha un\'assegnazione su questo settore: la filiera si interrompe qui.',
  max_depth: 'Profondità massima della catena raggiunta: la filiera potrebbe essere più lunga o ciclica.',
  cycle: 'Ciclo rilevato nella gerarchia: la filiera si interrompe per evitare un pagamento doppio.',
  assegnazioni_incoerenti: 'Più assegnazioni si sovrappongono per lo stesso utente/settore/data: dato incoerente.',
}

const COMMISSION_SOURCE_LABELS: Record<string, string> = {
  override: 'Override personale approvato',
  listino: 'Listino',
  nessuna: 'Nessuna (passaggio)',
  listino_incoerente: 'Listino incoerente',
}

function formatPct(n: number | null | undefined): string {
  if (n == null) return '—'
  return `${n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`
}

/**
 * TASK-0268 (F-014). Mirrors `PolicyNames.Admin`'s `RequireRole(...)` set (`EqualCrmPolicies.cs`)
 * exactly — the UI affordance only (whether "Calcola e salva" is even shown); the server is the
 * actual boundary (`CommissionCalculationRunsCompositionController.CreateRun`'s own
 * `[Authorize(PolicyNames.Admin)]`). Same page-local, duplicated-on-purpose shape as
 * `CommissionPlanRatesPage.tsx`'s own `isAdmin` — copied, not imported, per this codebase's
 * established convention for this exact check (that page's own doc comment on it).
 */
function isAdmin(auth: LoginResponse | null): boolean {
  return auth !== null && ['Administrator', 'EqualAdmin'].includes(auth.role)
}

/**
 * TASK-0274 (F-020, REQ-083-001). Mirrors `PolicyNames.BackOffice`'s `RequireRole(...)` set
 * (`EqualCrmPolicies.cs`) exactly — same "UI affordance only, the server is the actual boundary"
 * convention as {@link isAdmin} right above (`ContractAttributionController.Put`'s own
 * `[Authorize(PolicyNames.BackOffice)]`).
 */
function isBackOfficeOrAdmin(auth: LoginResponse | null): boolean {
  return auth !== null
    && ['Administrator', 'EqualAdmin', 'EqualOperatore', 'EqualBackOffice'].includes(auth.role)
}

/**
 * F-135. Mirrors `PolicyNames.Agent`'s `RequireRole(...)` set: the external roles that create the practice but
 * do not pass `PolicyNames.EqualStaff`. Same "UI affordance only, the server is the boundary" convention as above.
 */
function isExternalAgent(auth: LoginResponse | null): boolean {
  return auth !== null && ['EqualAgente', 'EqualAgenzia', 'EqualSegnalatore'].includes(auth.role)
}

/** Mirrors `PolicyNames.EqualStaff` (`EqualCrmPolicies.cs`): who may duplicate a practice. UI affordance only. */
function isEqualStaff(auth: LoginResponse | null): boolean {
  return auth !== null && ['Administrator', 'EqualAdmin', 'EqualDirettoreGenerale', 'EqualDirettoreCommerciale',
    'EqualAreaManager', 'EqualOperatore', 'EqualBackOffice'].includes(auth.role)
}

export function ContractDetailPage() {
  const { id: idStr } = useParams<{ id: string }>()
  const [revealKey, setRevealKey] = useState(0) // C-08: rimonta i campi dinamici dopo la visione motivata
  const id = parseInt(idStr ?? '', 10)
  const location = useLocation()
  const navigate = useNavigate()

  const [contract, setContract] = useState<Contract | null>(null)
  // IMP-1: elenco dei mandanti, per mostrare il nome di quello della pratica.
  const [mandanti, setMandanti] = useState<{ id: number; name: string }[]>([])
  useEffect(() => {
    api<{ items: { id: number; name: string }[] }>('/api/mandanti').then(r => setMandanti(r.items)).catch(() => setMandanti([]))
  }, [])
  const [operators, setOperators] = useState<Operator[]>([])
  useEffect(() => {
    fetchOperators().then(r => setOperators(r.items)).catch(() => setOperators([]))
  }, [])
  const prompt = usePrompt()
  const [notes, setNotes] = useState<ContractNote[]>([])
  // Note in un riquadro scorrevole: all'apertura e a ogni nota nuova si mostra la più recente (in fondo).
  const notesBoxRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const el = notesBoxRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [notes.length])
  const [statusHistory, setStatusHistory] = useState<ContractStatusHistoryEntry[]>([])
  // Fase vera della card sul portale (dal riquadro «Portale»): il «!» accanto allo stato.
  const [portalPhase, setPortalPhase] = useState<PortalPhase | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // F-074 (TASK-0328): esito della verifica prima dell'invio al backoffice — i blocchi (422) o gli avvisi (200).
  const [submissionReport, setSubmissionReport] = useState<{
    kind: 'blocked' | 'warnings'
    title: string
    blocking: SubmissionFinding[]
    warnings: SubmissionFinding[]
  } | null>(null)
  const [notFound, setNotFound] = useState(false)

  // TASK-0048 independent review fix: ContractFormPage's `handleSave` navigates here right after
  // Create succeeds but the follow-up PATCH /{id}/amounts fails — it cannot show that warning
  // itself (setting local state right before `navigate()` is a no-op: React Router unmounts
  // ContractFormPage as part of the same navigation, discarding the state before it ever renders,
  // verified empirically with headless Chrome). The message instead travels via React Router's
  // navigation `state`, read here once on mount. Captured into local state (not read directly from
  // `location.state` on every render) so it survives the `loadContract()` re-render below, and the
  // history entry's `state` is cleared via a `replace` navigation right after so a later page
  // reload of this same URL does not resurface a stale warning from a previous visit.
  const [amountsWarning, setAmountsWarning] = useState<string | null>(
    (location.state as { amountsWarning?: string } | null)?.amountsWarning ?? null,
  )
  useEffect(() => {
    if (location.state) {
      navigate(location.pathname, { replace: true, state: null })
    }
    // Only ever needs to run once, right after landing here with a warning in the router state —
    // re-running on every `location`/`navigate` identity change would immediately clear the
    // warning it just captured above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [noteText, setNoteText] = useState('')
  const [noteInternal, setNoteInternal] = useState(false)
  const [addingNote, setAddingNote] = useState(false)
  const [updatingStatus, setUpdatingStatus] = useState(false)

  // TASK-0082: "Registra incasso" modal. Prefilled from the contract already loaded on this page —
  // the GET that renders the badges already carries paymentStatus/paidAmount/paidAt, so opening the
  // form needs no extra fetch.
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [savingPayment, setSavingPayment] = useState(false)
  const [paymentForm, setPaymentForm] = useState({ paymentStatus: 'unpaid', paidAmount: '', paidAt: '' })

  // "Modifica importi": unico punto dopo la creazione per impostare importo base e addon
  // (PATCH /amounts, solo staff). Senza, una pratica senza importo resta bloccata dalla completezza.
  const [amountsForm, setAmountsForm] = useState<{ baseAmount: string, addonTotal: string } | null>(null)
  const [savingAmounts, setSavingAmounts] = useState(false)

  // "Modifica pratica" (Bozza / Integrazione richiesta, solo staff): tipo, segmento, reparto, operatore, prodotto
  // e mandante, ad esempio dopo "Duplica". I prodotti si ricaricano quando cambiano reparto o operatore: scelto
  // l'operatore, si vedono solo i suoi prodotti (l'operatore della pratica resta quello del prodotto).
  const [detailsForm, setDetailsForm] = useState<{
    contractType: string, customerSegment: string, departmentId: string, operatorId: string, productId: string, mandanteId: string
  } | null>(null)
  const [savingDetails, setSavingDetails] = useState(false)
  const [detailsError, setDetailsError] = useState<string | null>(null)
  const [departments, setDepartments] = useState<Department[]>([])
  const [detailsProducts, setDetailsProducts] = useState<Product[]>([])
  const detailsDepartment = detailsForm?.departmentId
  const detailsOperator = detailsForm?.operatorId
  useEffect(() => {
    if (detailsDepartment === undefined || detailsOperator === undefined) return
    let cancelled = false
    fetchSelectableProducts(detailsDepartment === '' ? undefined : Number(detailsDepartment), undefined,
      detailsOperator === '' ? undefined : Number(detailsOperator))
      .then(items => { if (!cancelled) setDetailsProducts(items) })
      .catch(() => { if (!cancelled) setDetailsProducts([]) })
    return () => { cancelled = true }
  }, [detailsDepartment, detailsOperator])

  // TASK-0204 (REQ-005-003): "Registra previsione" modal — same shape as the payment modal above,
  // prefilled from the contract already loaded on this page (probability/expectedCloseDate are part
  // of every GET /api/contracts/{id} response, in every field-visibility branch — see api/contracts.ts).
  const [showForecastModal, setShowForecastModal] = useState(false)
  const [savingForecast, setSavingForecast] = useState(false)
  const [forecastError, setForecastError] = useState<string | null>(null)
  const [forecastForm, setForecastForm] = useState({ probability: '', expectedCloseDate: '' })

  // TASK-0274 (F-020, REQ-083-001): "Attribuzione" — a SEPARATE, independently-failing fetch from
  // loadContract() above, same "own fetch, tolerant of 404" shape as commissionPreview/commissionRuns:
  // GET /api/contracts/{id}/attribution carries the same ADR-008 visibility gate as the contract
  // itself, so an invisible contract answers 404 here too and this card is simply omitted rather than
  // blanking the rest of the page.
  const [attribution, setAttribution] = useState<ContractAttribution | null>(null)
  const [attributionLoading, setAttributionLoading] = useState(true)
  const [attributionError, setAttributionError] = useState<string | null>(null)

  // The selector options for the three editable roles. GET /api/users (UsersController) is
  // [Authorize(PolicyNames.Admin)] ONLY — narrower than this page's own
  // [Authorize(PolicyNames.BackOffice)] write gate, so a BackOffice-tier (non-Admin) caller who IS
  // allowed to PUT the attribution cannot list users through it. `usersError` records that so the
  // modal can degrade to a manual numeric-id input instead of an empty/broken dropdown — see this
  // task's own report for why no new backend endpoint was added to close this gap.
  const [users, setUsers] = useState<DirectoryUser[]>([])
  const [usersError, setUsersError] = useState<string | null>(null)

  const [showAttributionModal, setShowAttributionModal] = useState(false)
  const [savingAttribution, setSavingAttribution] = useState(false)
  const [attributionSaveError, setAttributionSaveError] = useState<string | null>(null)
  const [attributionForm, setAttributionForm] = useState({
    contractOwnerUserId: '',
    leadGeneratorUserId: '',
    appointmentSetterUserId: '',
    salesCloserUserId: '',
  })

  function loadContract() {
    // F-099: una pratica nel cestino si legge (con deletedAt) ma note e storico stati rispondono 404: non si
    // chiedono, cosi' la scheda non sparisce. La cronologia unificata (ContractTimeline) resta leggibile.
    return contractsApi.get(id).then(async contractData => {
      if (contractData.deletedAt) {
        setContract(contractData)
        setNotes([])
        setStatusHistory([])
        return
      }
      const [notesData, historyData] = await Promise.all([
        contractsApi.getNotes(id),
        contractsApi.getStatusHistory(id),
      ])
      setContract(contractData)
      setNotes(notesData)
      setStatusHistory(historyData)
    })
  }

  // F-099 (REQ-287-001): "Sposta nel cestino" con motivo (obbligatorio se non in Bozza) e blocchi sulle dipendenze.
  const [showTrashForm, setShowTrashForm] = useState(false)
  const [trashReason, setTrashReason] = useState('')
  const [trashBusy, setTrashBusy] = useState(false)
  const [duplicating, setDuplicating] = useState(false)
  const [syncTick, setSyncTick] = useState(0)

  /** Nuova pratica in Bozza sullo stesso cliente: copia solo anagrafica, agente e IBAN, il resto riparte da zero. */
  async function duplicate() {
    if (!window.confirm('Duplicare la pratica? Si crea una nuova bozza con gli stessi cliente, agente e IBAN. '
      + 'Prodotto, importi, forniture (POD/PDR) e stati ripartono da zero: completali con "Modifica pratica" e "Modifica importi".')) return
    setDuplicating(true)
    try {
      const r = await api<{ id: number }>(`/api/contracts/${id}/duplica`, { method: 'POST', body: '{}' })
      navigate(`/contracts/${r.id}`)
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Duplicazione non riuscita')
    } finally {
      setDuplicating(false)
    }
  }
  const [trashError, setTrashError] = useState<string | null>(null)
  const [trashDeps, setTrashDeps] = useState<Record<string, number> | null>(null)

  async function submitTrash(e: React.FormEvent) {
    e.preventDefault()
    setTrashBusy(true)
    setTrashError(null)
    setTrashDeps(null)
    try {
      await contractsApi.delete(id, trashReason)
      setShowTrashForm(false)
      setTrashReason('')
      await loadContract()
    } catch (err) {
      if (err instanceof ApiError && err.code === 'HAS_DEPENDENCIES') {
        const deps = (err.body as { dependencies?: Record<string, number> } | undefined)?.dependencies
        setTrashDeps(deps ?? {})
      }
      setTrashError(err instanceof Error ? err.message : 'Operazione non riuscita')
    } finally {
      setTrashBusy(false)
    }
  }

  useEffect(() => {
    if (isNaN(id)) {
      setError('ID contratto non valido.')
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    setNotFound(false)

    loadContract()
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err)
        if (msg.includes('404') || msg.toLowerCase().includes('not found')) {
          setNotFound(true)
        } else {
          setError(msg || 'Errore nel caricamento del contratto.')
        }
      })
      .finally(() => setLoading(false))
  }, [id])

  // TASK-0267 (F-013, P-21a): "Riparto provvigionale (anteprima)" — a SEPARATE, independently
  // failing fetch from `loadContract()` above. Deliberately not folded into that Promise.all: this
  // endpoint is gated to Admin/EqualStaff WITH `CanSeeRealAmounts` (ADR-008 + REQ-058-012), so a
  // caller without that flag getting a 403 here must never blank out the rest of the page (the
  // 403 branch below hides only this one card). TASK-0267 review correction: a missing `SignedAt`
  // is now a normal `200` with `outcome: 'signed_at_mancante'` (this module's own consolidated
  // convention for a diagnostic GET — never a `4xx`), so it flows through `commissionPreview` like
  // any other outcome and is rendered via the SAME curated `chain.truncation` label as every other
  // "chain not resolvable" case, not a separate raw-message alert.
  const [commissionPreview, setCommissionPreview] = useState<CommissionPreview | null>(null)
  const [commissionPreviewError, setCommissionPreviewError] = useState<string | null>(null)
  const [commissionPreviewLoading, setCommissionPreviewLoading] = useState(true)

  useEffect(() => {
    if (isNaN(id)) return
    setCommissionPreviewLoading(true)
    setCommissionPreview(null)
    setCommissionPreviewError(null)

    fetchCommissionPreview(id)
      .then(setCommissionPreview)
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err)
        // 403 (no CanSeeRealAmounts / Agent-tier) and 404 (invisible/nonexistent, ADR-008 point 3
        // "indistinguishable from nonexistent"): this caller simply has no business seeing this
        // card at all — omitted silently, same as every other permission-gated section on this
        // page relies on the backend's own redaction rather than a client-side role check. Every
        // OTHER outcome this endpoint can produce (including a missing SignedAt) is a 200, so
        // reaching this branch at all now means something genuinely unexpected happened.
        if (msg.toLowerCase().includes('forbidden') || msg.toLowerCase().includes('not found')) {
          return
        }
        setCommissionPreviewError(msg || 'Impossibile calcolare l\'anteprima del riparto.')
      })
      .finally(() => setCommissionPreviewLoading(false))
  }, [id])

  // TASK-0268 (F-014, P-21b — REQ-019-002, REQ-016-001): "Storico calcoli" — unlike the preview
  // above, GET /api/contracts/{id}/commission-runs reads back calculations that were actually
  // PERSISTED. Same "own, independently-failing fetch" shape as commissionPreview: a 403/404 (no
  // CanSeeRealAmounts, or an invisible contract — this endpoint only requires Authenticated, but the
  // money fields it carries are still gated) never blanks the rest of the page.
  const [commissionRuns, setCommissionRuns] = useState<CommissionRunSummary[]>([])
  const [commissionRunsLoading, setCommissionRunsLoading] = useState(true)
  const [commissionRunsError, setCommissionRunsError] = useState<string | null>(null)

  // Admin-only "Calcola e salva" (POST) state — see CreateRun's own remarks for the write it performs.
  const [auth, setAuth] = useState<LoginResponse | null>(null)
  const [creatingRun, setCreatingRun] = useState(false)
  const [createRunResult, setCreateRunResult] = useState<CommissionRunCreateResult | null>(null)
  const [createRunError, setCreateRunError] = useState<string | null>(null)

  // Row-by-row explanation ("perché questo importo"), fetched on demand per run rather than for
  // every row in the list up front — GET /api/commission-runs/{runId} is a second round trip per
  // run, and most of the time a user only wants to inspect the ONE run they just created or are
  // currently curious about, not every historical row at once.
  const [expandedRunId, setExpandedRunId] = useState<number | null>(null)
  const [runDetail, setRunDetail] = useState<CommissionRunDetail | null>(null)
  const [runDetailLoading, setRunDetailLoading] = useState(false)
  const [runDetailError, setRunDetailError] = useState<string | null>(null)

  const loadCommissionRuns = useCallback(() => {
    if (isNaN(id)) return
    setCommissionRunsLoading(true)
    setCommissionRunsError(null)

    fetchCommissionRuns(id)
      .then(setCommissionRuns)
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err)
        if (msg.toLowerCase().includes('forbidden') || msg.toLowerCase().includes('not found')) {
          setCommissionRuns([])
          return
        }
        setCommissionRunsError(msg || 'Impossibile caricare lo storico dei calcoli.')
      })
      .finally(() => setCommissionRunsLoading(false))
  }, [id])

  useEffect(() => { loadCommissionRuns() }, [loadCommissionRuns])

  useEffect(() => {
    fetchMe().then(setAuth).catch(() => setAuth(null))
  }, [])

  // TASK-0274 (F-020, REQ-083-001).
  const loadAttribution = useCallback(() => {
    if (isNaN(id)) return
    setAttributionLoading(true)
    setAttributionError(null)
    contractsApi.getAttribution(id)
      .then(setAttribution)
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err)
        if (msg.toLowerCase().includes('not found')) {
          setAttribution(null)
          return
        }
        setAttributionError(msg || 'Impossibile caricare l\'attribuzione del contratto.')
      })
      .finally(() => setAttributionLoading(false))
  }, [id])

  useEffect(() => { loadAttribution() }, [loadAttribution])

  // TASK-0281 (F-027, REQ-035-001): "Completezza pratica" — a SEPARATE, independently-failing fetch,
  // same shape as attribution/commissionPreview above. GET /api/contracts/{id}/readiness carries the
  // same ADR-008 visibility gate as the contract itself (404 never 403), so reaching that branch here
  // would be unexpected once the contract itself has already loaded on this page — kept anyway for the
  // same defensive reason commissionPreview keeps it. The one outcome this task's own delivery notes
  // call out as a near-certainty TODAY is neither of those: F-026's migration is not yet applied in
  // production, so `crm.validation_rules` does not exist yet and this endpoint answers a genuine
  // server error. That must never render as a raw exception message on a page an agent opens dozens
  // of times a day — `readinessUnavailable` renders one calm, fixed sentence instead, regardless of
  // the underlying cause (a missing table today, anything else tomorrow): this client cannot reliably
  // tell those apart from the error text alone, and guessing would risk asserting a technical detail
  // it never actually confirmed.
  const [readiness, setReadiness] = useState<ContractReadiness | null>(null)
  const [readinessLoading, setReadinessLoading] = useState(true)
  const [readinessUnavailable, setReadinessUnavailable] = useState(false)
  const [readinessTick, setReadinessTick] = useState(0)
  // Pratiche salvate senza cliente o senza data di firma: si sistemano da qui (requisiti per il caricamento sui portali).
  const [linkingCustomer, setLinkingCustomer] = useState(false)
  const [customerChoices, setCustomerChoices] = useState<{ id: number; label: string }[]>([])
  // Collegamento a mano: ricerca in anagrafica (nome, ragione sociale, CF, P.IVA, codice) e scelta dall'elenco.
  const [customerSearch, setCustomerSearch] = useState('')
  const [customerResults, setCustomerResults] = useState<Customer[]>([])
  const [customerLinkNotice, setCustomerLinkNotice] = useState<string | null>(null)
  useEffect(() => {
    const term = customerSearch.trim()
    if (!term) { setCustomerResults([]); return }
    const t = setTimeout(() => {
      customersApi.list({ search: term, size: 10 }).then(r => setCustomerResults(r.items)).catch(() => setCustomerResults([]))
    }, 300)
    return () => clearTimeout(t)
  }, [customerSearch])
  const [signedAtDraft, setSignedAtDraft] = useState<string | null>(null)
  const [savingSignedAt, setSavingSignedAt] = useState(false)
  // Pratica con cliente ma senza anagrafica (salvata prima del collegamento automatico): si collega una volta all'apertura.
  const [contactLinkError, setContactLinkError] = useState<string | null>(null)
  const [contactLinkTried, setContactLinkTried] = useState(false)

  useEffect(() => {
    if (!contract || contract.deletedAt || !contract.customerId || contract.contactId || contactLinkTried) return
    setContactLinkTried(true)
    contractsApi.linkContact(id)
      .then(async () => {
        setContactLinkError(null)
        await loadContract()
        setReadinessTick(t => t + 1)
      })
      .catch((err: unknown) => setContactLinkError(err instanceof Error ? err.message : String(err)))
    // loadContract cambia a ogni render: il tentativo è uno per pratica (contactLinkTried).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract, contactLinkTried, id])

  useEffect(() => {
    if (isNaN(id)) return
    setReadinessLoading(true)
    setReadiness(null)
    setReadinessUnavailable(false)

    contractsApi.getReadiness(id)
      .then(setReadiness)
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err)
        if (msg.toLowerCase().includes('not found')) {
          // Same "invisible == nonexistent" ADR-008 read as everywhere else: the rest of the page
          // already shows this contract, so this branch should not really be reachable — but if it
          // ever is, the card simply omits itself rather than showing a contradiction.
          return
        }
        // Review fix (P1 follow-up): the user-facing message is deliberately a fixed, calm sentence
        // (see the block comment above) rather than `msg` — but that must not mean the real error
        // vanishes entirely. Every sibling fetch on this page (attribution/commissionPreview/
        // commissionRuns) surfaces `msg` somewhere a developer can see it; this one hid it nowhere,
        // which means a genuine bug after the migration lands would show the same reassuring text and
        // leave no trace to find it by. Logged, not surfaced to the user.
        console.error('GET /api/contracts/{id}/readiness failed:', err)
        setReadinessUnavailable(true)
      })
      .finally(() => setReadinessLoading(false))
  }, [id, readinessTick])

  const assignCustomer = async (customerId: number) => {
    await contractsApi.setCustomer(id, customerId)
    setCustomerChoices([])
    setCustomerSearch('')
    setCustomerResults([])
    setContactLinkError(null)
    await loadContract()
    setReadinessTick(t => t + 1)
  }

  const customerLabel = (f: Customer) =>
    `${f.customerCode ?? '#' + f.id} — ${f.companyName || [f.firstName, f.lastName].filter(Boolean).join(' ')}`
  // Etichette dei clienti candidati (dal controllo duplicati del server); chi non si può leggere resta con il solo id.
  const choicesFor = async (ids: number[]) => {
    const rows = await Promise.all(ids.map(cid => customersApi.get(cid).then(customerLabel).catch(() => `#${cid}`)))
    setCustomerChoices(ids.map((cid, i) => ({ id: cid, label: rows[i]! })))
  }

  // Stessa logica del salvataggio della pratica: cerca per P.IVA/CF dei dati portale, se c'è un solo cliente lo
  // collega, se ce ne sono più fa scegliere, altrimenti lo crea dai dati portale. Se il server segnala un doppione
  // (anche per email) si collega il cliente indicato invece di fermarsi.
  const linkCustomerFromDocs = async () => {
    setLinkingCustomer(true)
    setError(null)
    setCustomerLinkNotice(null)
    try {
      const portal = await contractsApi.getPortalData(id)
      const ident = docIdentity({ partitaIva: portal['PARTITA_IVA_CLIENTE'], codiceFiscale: portal['CODICE_FISCALE_CLIENTE'] })
      const found = ident ? (await customersApi.list({ search: ident, size: 10 })).items : []
      if (found.length > 1) {
        setCustomerChoices(found.map(f => ({ id: f.id, label: customerLabel(f) })))
        return
      }
      if (found.length === 1) { await assignCustomer(found[0].id); return }
      const data = customerFromDocs(portal, {})
      if (!data) { setError('Dati portale insufficienti per creare il cliente: compila nome/ragione sociale e CF/P.IVA.'); return }
      const outcome = await createOrLinkCustomer((d, k) => customersApi.create(d, k), data, `contract-${id}-cust`)
      if (outcome.kind === 'choose') {
        await choicesFor(outcome.ids)
        setCustomerLinkNotice('Più clienti in anagrafica hanno gli stessi dati (CF, P.IVA o email): scegli quello giusto.')
        return
      }
      await assignCustomer(outcome.id)
      if (outcome.kind === 'linked') setCustomerLinkNotice('Il cliente era già in anagrafica: collegato quello esistente.')
      else if (outcome.forced) setCustomerLinkNotice('Cliente creato. Un contatto con gli stessi dati esisteva fuori dall\'anagrafica clienti: controlla che non sia un doppione.')
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setLinkingCustomer(false)
    }
  }

  // Codice portale a mano (es. numero della bozza Ermes creata dal portale): con il codice compaiono "Aggiorna la bozza"
  // e il collegamento con gli stati letti dal portale.
  const editOperatorReferenceId = async () => {
    const value = await prompt({
      title: 'Codice della pratica sul portale',
      message: 'Per Ermes è il numero della bozza ContrattiSmart (es. EHF26A/0000096), per Teamwork l\'ID pratica. Vuoto per toglierlo.',
      label: 'Codice portale',
      initialValue: contract?.operatorReferenceId ?? '',
      confirmLabel: 'Salva',
    })
    if (value === null) return
    setError(null)
    try {
      await contractsApi.setOperatorReferenceId(id, value.trim() || null)
      await loadContract()
      setReadinessTick(t => t + 1)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  const saveSignedAt = async () => {
    if (signedAtDraft === null) return
    setSavingSignedAt(true)
    setError(null)
    try {
      await contractsApi.setSignedAt(id, signedAtDraft || null)
      setSignedAtDraft(null)
      await loadContract()
      setReadinessTick(t => t + 1)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setSavingSignedAt(false)
    }
  }

  // TASK-0274: the selector's option list — see the `users`/`usersError` state declaration above for
  // why a 403 here (BackOffice-tier caller) is expected and handled, not an error condition for the
  // rest of the page.
  useEffect(() => {
    userDirectoryApi.list({ activeOnly: false, limit: 200 })
      .then(setUsers)
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err)
        setUsersError(msg || 'Impossibile caricare l\'elenco utenti.')
      })
  }, [])

  async function handleCreateCommissionRun() {
    setCreatingRun(true)
    setCreateRunError(null)
    setCreateRunResult(null)
    try {
      const result = await createCommissionRun(id)
      setCreateRunResult(result)
      // A genuinely new run (computable && !unchanged) changes the list AND, per this card's own
      // "congelamento se ancora nulla", may have just frozen Contract.CommissionBasis for the first
      // time — reload the contract too so the rest of the page (which reads baseAmount elsewhere)
      // never shows a stale, pre-freeze value.
      if (result.computable) {
        loadCommissionRuns()
        loadContract().catch(() => { /* the page's own contract card already shows any load error */ })
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err)
      setCreateRunError(msg || 'Impossibile calcolare e salvare il run.')
    } finally {
      setCreatingRun(false)
    }
  }

  function toggleRunDetail(runId: number) {
    if (expandedRunId === runId) {
      setExpandedRunId(null)
      setRunDetail(null)
      setRunDetailError(null)
      return
    }

    setExpandedRunId(runId)
    setRunDetail(null)
    setRunDetailError(null)
    setRunDetailLoading(true)
    fetchCommissionRunDetail(runId)
      .then(setRunDetail)
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err)
        setRunDetailError(msg || 'Impossibile caricare il dettaglio del run.')
      })
      .finally(() => setRunDetailLoading(false))
  }

  async function handleAddNote() {
    if (!noteText.trim()) return
    setAddingNote(true)
    try {
      const n = await contractsApi.addNote(id, noteText.trim(), noteInternal)
      setNotes(prev => [...prev, n])
      setNoteText('')
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err)
      setError(msg || 'Errore nell\'aggiunta della nota.')
    } finally {
      setAddingNote(false)
    }
  }

  async function handleStatusChange(newStatus: string) {
    if (!contract || updatingStatus) return
    // TASK-0025: only ever called with a value from contract.availableStatusTransitions (see
    // renderStatusButtons below), so this request should always be a valid transition — but the
    // backend is still the one source of truth, and answers 400 on anything it disagrees with.
    //
    // F-035 review fix: window.prompt returns `null` when the user clicks "Annulla"/presses Escape,
    // and an empty string when they confirm an intentionally blank reason — those are NOT the same
    // thing. The old `?? undefined` coalesced both into "no reason" and fell through to submit the
    // transition regardless, so a misclick on a status button followed by "Annulla" on the prompt
    // still fired the transition. Now that several statuses are terminal with no way back
    // (Stornata/Sospesa/Annullata), that silent fallthrough is no longer safely correctable — a `null`
    // must abort the whole transition, not just the reason.
    const promptResult = await prompt({
      title: 'Cambia lo stato della pratica',
      message: 'Motivo del cambio di stato (opzionale).',
      label: 'Motivo',
      initialValue: '',
      multiline: true,
      confirmLabel: 'Cambia stato',
    })
    if (promptResult === null) return // user cancelled the prompt: do not submit the transition at all
    const reason = promptResult
    setUpdatingStatus(true)
    setError(null)
    setSubmissionReport(null)
    try {
      if (newStatus === 'InviataAlBackoffice') {
        // F-074: l'invio passa dalla verifica di campi, formati, documenti e prodotto. Con il controllo
        // imposto i blocchi tornano come 422 (stato invariato); altrimenti la pratica parte e gli avvisi
        // (200) si mostrano qui sotto.
        const result = await submitToBackoffice(id, reason || undefined, isExternalAgent(auth))
        // SIM collegate (bundle F+M o fisso con SIM): partono insieme alla principale.
        const simIssues: SubmissionFinding[] = []
        try {
          const portal = await contractsApi.getPortalData(id)
          for (const simId of (portal[LINKED_PRACTICES_KEY] ?? '').split(',').map(Number).filter(n => n > 0)) {
            try {
              const sim = await contractsApi.get(simId)
              if (sim.status === 'Bozza') await submitToBackoffice(simId, reason || undefined, isExternalAgent(auth))
            } catch (sErr) {
              const blocked = submitBlockedFrom(sErr)
              simIssues.push({
                source: 'sim', code: `sim-${simId}`,
                message: `SIM collegata ${simId} non inviata: ${blocked ? blocked.error : sErr instanceof Error ? sErr.message : String(sErr)}`,
              })
            }
          }
        } catch { /* senza dati portale non ci sono SIM collegate da inviare */ }
        const warnings = [...(result?.warnings ?? []), ...simIssues]
        if (warnings.length > 0) {
          setSubmissionReport({
            kind: 'warnings',
            title: 'La pratica è stata inviata al backoffice, ma la verifica ha rilevato queste lacune:',
            blocking: [],
            warnings,
          })
        }
      } else {
        await contractsApi.updateStatus(id, newStatus, reason || undefined)
      }
      // Re-fetch rather than patch the local status in place: the contract's
      // availableStatusTransitions must be recomputed from the NEW status, and the status-history
      // table needs the row the backend just wrote.
      await loadContract()
    } catch (err: unknown) {
      const blocked = submitBlockedFrom(err)
      if (blocked) {
        setSubmissionReport({ kind: 'blocked', title: blocked.error, blocking: blocked.blocking, warnings: blocked.warnings })
        return
      }
      const msg = err instanceof Error ? err.message : String(err)
      setError(msg || "Errore nell'aggiornamento dello stato.")
    } finally {
      setUpdatingStatus(false)
    }
  }

  /**
   * TASK-0082: opens the payment modal prefilled with the contract's CURRENT values, so a partial
   * incasso can be topped up rather than retyped from scratch. `paidAmount` is held as a string
   * (an empty input must stay empty, not become `0`) and `paidAt` is already a bare `YYYY-MM-DD`
   * server-side (`DateOnly?`), which is exactly what `<input type="date">` wants — but a defensive
   * `.slice(0, 10)` keeps it correct if that ever serializes as a full instant.
   */
  function openPaymentModal() {
    if (!contract) return
    setPaymentForm({
      paymentStatus: contract.paymentStatus || 'unpaid',
      paidAmount: contract.paidAmount != null ? String(contract.paidAmount) : '',
      paidAt: contract.paidAt ? contract.paidAt.slice(0, 10) : '',
    })
    setShowPaymentModal(true)
  }

  async function handleSavePayment() {
    if (!contract || savingPayment) return
    setSavingPayment(true)
    setError(null)
    try {
      await contractsApi.updatePayment(id, {
        // PaidAmount is non-nullable server-side; an empty input means "nothing collected yet", i.e. 0.
        paidAmount: paymentForm.paidAmount.trim() === '' ? 0 : Number(paymentForm.paidAmount),
        paymentStatus: paymentForm.paymentStatus,
        // `exactOptionalPropertyTypes` is on: the key is omitted entirely when the date is blank
        // (JSON.stringify drops it, the server binds DateOnly? as null) rather than set to
        // `undefined`, which that flag rejects for an optional property.
        ...(paymentForm.paidAt ? { paidAt: paymentForm.paidAt } : {}),
      })
      setShowPaymentModal(false)
      // Re-fetch rather than patch the local paymentStatus in place: the endpoint answers 204 with no
      // body, and both "Stato pagamento" badges (header and the Dettagli row) must render the value
      // the server actually persisted, not the one this page hoped it sent.
      await loadContract()
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err)
      setError(msg || "Errore nella registrazione dell'incasso.")
    } finally {
      setSavingPayment(false)
    }
  }

  function openAmountsModal() {
    if (!contract) return
    setAmountsForm({
      baseAmount: contract.baseAmount != null ? String(contract.baseAmount) : '',
      addonTotal: contract.addonTotal != null ? String(contract.addonTotal) : '',
    })
  }

  function openDetailsModal() {
    if (!contract) return
    setDetailsError(null)
    setDetailsForm({
      contractType: contract.contractType ?? '',
      customerSegment: contract.customerSegment ?? '',
      departmentId: contract.departmentId != null ? String(contract.departmentId) : '',
      operatorId: contract.operatorId != null ? String(contract.operatorId) : '',
      productId: contract.productId != null ? String(contract.productId) : '',
      mandanteId: contract.mandanteId != null ? String(contract.mandanteId) : '',
    })
    fetchDepartments().then(setDepartments).catch(() => setDepartments([]))
  }

  async function handleSaveDetails() {
    if (!contract || !detailsForm || savingDetails) return
    const productId = detailsForm.productId === '' ? null : Number(detailsForm.productId)
    const mandanteId = detailsForm.mandanteId === '' ? null : Number(detailsForm.mandanteId)
    setSavingDetails(true)
    setDetailsError(null)
    try {
      await contractsApi.setDetails(id, {
        contractType: detailsForm.contractType.trim() === '' ? null : detailsForm.contractType.trim(),
        customerSegment: detailsForm.customerSegment === '' ? null : detailsForm.customerSegment as 'consumer' | 'business',
        departmentId: detailsForm.departmentId === '' ? null : Number(detailsForm.departmentId),
        productId: productId !== (contract.productId ?? null) ? productId : null,
      })
      if (mandanteId !== (contract.mandanteId ?? null)) await contractsApi.setMandante(id, mandanteId)
      setDetailsForm(null)
      await loadContract()
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err)
      setDetailsError(msg || 'Errore nel salvataggio dei dati della pratica.')
      await loadContract()
    } finally {
      setSavingDetails(false)
    }
  }

  async function handleSaveAmounts() {
    if (!contract || !amountsForm || savingAmounts) return
    const baseAmount = amountsForm.baseAmount.trim() === '' ? 0 : Number(amountsForm.baseAmount)
    const addonTotal = amountsForm.addonTotal.trim() === '' ? 0 : Number(amountsForm.addonTotal)
    if (!Number.isFinite(baseAmount) || !Number.isFinite(addonTotal) || baseAmount < 0 || addonTotal < 0) {
      setError('Gli importi devono essere numeri non negativi.')
      return
    }
    setSavingAmounts(true)
    setError(null)
    try {
      await contractsApi.updateAmounts(id, {
        baseAmount,
        addonTotal,
        ...(contract.agentVisibleAmount != null ? { agentVisibleAmount: contract.agentVisibleAmount } : {}),
      })
      setAmountsForm(null)
      await loadContract()
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err)
      setError(msg || 'Errore nel salvataggio degli importi.')
    } finally {
      setSavingAmounts(false)
    }
  }

  /**
   * TASK-0204 (REQ-005-003): opens the forecast modal prefilled with the contract's CURRENT values —
   * same "prefill from what's already loaded" convention as {@link openPaymentModal}.
   * `expectedCloseDate` is already a bare `YYYY-MM-DD` server-side (`DateOnly?`), exactly what
   * `<input type="date">` wants; the defensive `.slice(0, 10)` matches {@link openPaymentModal}'s own
   * for the same reason.
   */
  function openForecastModal() {
    if (!contract) return
    setForecastError(null)
    setForecastForm({
      probability: contract.probability != null ? String(contract.probability) : '',
      expectedCloseDate: contract.expectedCloseDate ? contract.expectedCloseDate.slice(0, 10) : '',
    })
    setShowForecastModal(true)
  }

  async function handleSaveForecast() {
    if (!contract || savingForecast) return
    setSavingForecast(true)
    setForecastError(null)
    try {
      await contractsApi.assignForecast(id, {
        probability: forecastForm.probability.trim() === '' ? null : Number(forecastForm.probability),
        expectedCloseDate: forecastForm.expectedCloseDate || null,
      })
      setShowForecastModal(false)
      // Re-fetch rather than patch local state in place — same reasoning as handleSavePayment: the
      // endpoint answers 204 with no body, and the Dettagli row must render what the server actually
      // persisted (including the 400 the backend answers for an out-of-range probability, which
      // never reaches this line — the catch below surfaces it in the modal instead of dismissing it).
      await loadContract()
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err)
      setForecastError(msg || 'Errore nella registrazione della previsione.')
    } finally {
      setSavingForecast(false)
    }
  }

  /**
   * TASK-0274 (F-020, REQ-083-001): resolves a user id to a display name, falling back to `#id` when
   * the id is not (yet, or ever — see `usersError`) in the loaded `users` list. `null`/`undefined`
   * render as '—', same convention as every other unset attribution on this page.
   */
  function userLabel(userId: number | null | undefined): string {
    if (userId == null) return '—'
    const match = users.find(u => u.id === userId)
    return match ? match.name : `#${userId}`
  }

  /**
   * TASK-0274: one field of the "Attribuzione" modal. A `<select>` of the loaded `users` when the
   * list could be fetched; a plain numeric-id `<input>` otherwise — see the `users`/`usersError` state
   * declaration above for why GET /api/users can 403 for a caller who is still allowed to PUT the
   * attribution, and why that degrades this field instead of hiding it.
   */
  function renderUserPicker(
    fieldId: string, label: string, value: string, onChange: (v: string) => void,
  ) {
    return (
      <>
        {usersError ? (
          <TextField
            id={fieldId}
            label={label}
            type="number"
            step="1"
            placeholder="ID utente"
            value={value}
            onChange={e => onChange(e.target.value)}
            disabled={savingAttribution}
          />
        ) : (
          <Select
            id={fieldId}
            label={label}
            value={value}
            onChange={e => onChange(e.target.value)}
            disabled={savingAttribution}
          >
            <option value="">— Nessuno —</option>
            {users.map(u => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </Select>
        )}
      </>
    )
  }

  /**
   * TASK-0274: opens the "Attribuzione" modal prefilled from the CURRENT values on
   * `attribution` — same "prefill from what's already loaded" convention as
   * {@link openPaymentModal}/{@link openForecastModal} — falling back to the linked lead's/
   * appointment's own `suggested` values (the card's own "precompilata dal lead o dall'appuntamento
   * collegati quando esistono") ONLY for a field that is still unset. A field that already carries a
   * value is never silently overridden by a suggestion.
   */
  function openAttributionModal() {
    if (!attribution) return
    setAttributionSaveError(null)
    setAttributionForm({
      contractOwnerUserId: String(attribution.contractOwnerUserId ?? ''),
      leadGeneratorUserId: String(
        attribution.leadGeneratorUserId ?? attribution.suggested.leadGeneratorUserId ?? ''),
      appointmentSetterUserId: String(
        attribution.appointmentSetterUserId ?? attribution.suggested.appointmentSetterUserId ?? ''),
      salesCloserUserId: String(
        attribution.salesCloserUserId ?? attribution.suggested.salesCloserUserId ?? ''),
    })
    setShowAttributionModal(true)
  }

  /**
   * TASK-0274: this modal always edits and resubmits all three fields together — see
   * `ContractAttributionUpdateRequest`'s own remarks for why that is a safe subset of what the
   * backend's absent-vs-null contract actually allows. An empty selector means "clear this role"
   * (`null`), never "leave it untouched" (omitted key), since every field IS represented in this form.
   */
  async function handleSaveAttribution() {
    if (savingAttribution) return
    setSavingAttribution(true)
    setAttributionSaveError(null)
    try {
      // Agente di riferimento (Contract.AgentId): endpoint a parte, chiamato solo se cambia.
      const owner = attributionForm.contractOwnerUserId === '' ? null : Number(attributionForm.contractOwnerUserId)
      if (owner !== (attribution?.contractOwnerUserId ?? null)) {
        await contractsApi.setAgent(id, owner)
        loadContract()
      }
      await contractsApi.updateAttribution(id, {
        leadGeneratorUserId: attributionForm.leadGeneratorUserId === ''
          ? null : Number(attributionForm.leadGeneratorUserId),
        appointmentSetterUserId: attributionForm.appointmentSetterUserId === ''
          ? null : Number(attributionForm.appointmentSetterUserId),
        salesCloserUserId: attributionForm.salesCloserUserId === ''
          ? null : Number(attributionForm.salesCloserUserId),
      })
      setShowAttributionModal(false)
      loadAttribution()
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err)
      setAttributionSaveError(msg || 'Errore nel salvataggio dell\'attribuzione.')
    } finally {
      setSavingAttribution(false)
    }
  }

  if (loading) {
    return <LoadingState label="Caricamento..." />
  }

  if (notFound) {
    return (
      <Callout tone="warning">
        Contratto non trovato.{' '}
        <Link to="/contracts">Torna alla lista</Link>
      </Callout>
    )
  }

  if (error) {
    return <Callout tone="error">{error}</Callout>
  }

  if (!contract) return null

  const c = contract
  // F-135: gli agenti esterni possono fare una sola transizione, Bozza -> Inviata al backoffice (POST /submit);
  // gli altri pulsanti di stato per loro non si mostrano (il server darebbe 403).
  const visibleTransitions = isExternalAgent(auth)
    ? c.availableStatusTransitions.filter(s => s === 'InviataAlBackoffice')
    : c.availableStatusTransitions

  // TASK-0162 (REQ-301-006). For a caller without `CanSeeRealAmounts` the server omits
  // `baseAmount`/`addonTotal`/`expectedAmount` outright and sends `agentVisibleAmount` in their
  // place. Two separate hazards are closed here, and both used to be live the moment `Get` started
  // redacting:
  //
  //   1. `c.baseAmount + c.addonTotal` on absent fields is `undefined + undefined` === `NaN`, which
  //      is NOT caught by `??` and renders as a confident, entirely fabricated "€ NaN". The sum is
  //      therefore only attempted when BOTH operands are actually present; otherwise the real total
  //      is `undefined`, per this task's explicit requirement — never NaN.
  //   2. The substitute is then applied on top, mirroring what `List` already does for a denied
  //      caller: show `agentVisibleAmount` rather than a blank. `formatEur` is null-safe now, so if
  //      even that is absent/null every read site degrades to '—' instead of throwing.
  //
  // `addonTotal` deliberately has NO substitute: `agentVisibleAmount` is a stand-in for the practice
  // TOTAL, not for its addon component, so putting it in the "Addon" row would state something false
  // about the contract. That row shows '—' for a denied caller, which is the honest answer.
  const realTotalAmount =
    c.expectedAmount ??
    (c.baseAmount != null && c.addonTotal != null ? c.baseAmount + c.addonTotal : undefined)
  const totalAmount = realTotalAmount ?? c.agentVisibleAmount
  const baseAmountDisplay = c.baseAmount ?? c.agentVisibleAmount
  // F-099: pratica nel cestino - si legge, ma le azioni e i pannelli che rispondono 404 non si mostrano.
  const inTrash = c.deletedAt != null
  // Stesso riferimento dell'elenco: il codice EQ si mostra com'e'; i vecchi CONT-IMP/CONT-MIGR diventano "Mandante · Operatore · data".
  const referenceLabel = contractReferenceLabel({
    id: c.id,
    referenceCode: c.referenceCode,
    mandante: mandanti.find(m => m.id === c.mandanteId)?.name,
    operator: operators.find(o => o.id === c.operatorId)?.name,
    date: formatDate(c.signedAt ?? c.createdAt),
  })

  const operatorName = operators.find(o => o.id === c.operatorId)?.name
  const mandanteName = c.mandanteId != null ? (mandanti.find(m => m.id === c.mandanteId)?.name ?? `#${c.mandanteId}`) : null
  const canEditPractice = !inTrash && isEqualStaff(auth) && (c.status === 'Bozza' || c.status === 'IntegrazioneRichiesta')
  // Il punteggio di completezza va in cima solo quando c'è qualcosa da sistemare; se è tutto a posto basta la pillola nel riepilogo.
  const readinessNeedsAttention = readinessUnavailable || (readiness != null && readiness.unmetRules.length > 0)
    || (contactLinkError != null && !!contract?.customerId && !contract.contactId)
  const moreActions = [
    ...(!inTrash && isEqualStaff(auth) ? [{ label: duplicating ? 'Duplicazione...' : 'Duplica', icon: 'content_copy', disabled: duplicating, onSelect: () => { void duplicate() } }] : []),
    ...(!inTrash && isEqualStaff(auth) ? [{ label: 'Modifica importi', icon: 'euro', onSelect: openAmountsModal }] : []),
    { label: 'Registra incasso', icon: 'payments', onSelect: openPaymentModal },
    { label: 'Previsione di chiusura', icon: 'trending_up', onSelect: openForecastModal },
    ...(isAdmin(auth) && !inTrash ? [{ label: 'Sposta nel cestino', icon: 'delete', onSelect: () => setShowTrashForm(true) }] : []),
  ]

  return (
    <Page>
      <PageHeader
        title={referenceLabel}
        back={{ to: '/contracts', label: 'Contratti' }}
        subtitle={[c.customerName, operatorName, mandanteName].filter(Boolean).join(' · ') || undefined}
        actions={
          <>
            {/* Legge subito stato e dati dal portale dell'operatore (Ermes, Teamwork, Coraly), oltre al giro ogni 10 minuti. */}
            {!inTrash && isEqualStaff(auth) && syncGestoreFor(operatorName, c.operatorId) && (
              <PortalSyncButton gestore={syncGestoreFor(operatorName, c.operatorId)!}
                contractId={c.id} onDone={() => { void loadContract(); setSyncTick(t => t + 1) }} />
            )}
            {canEditPractice && (
              <Button type="button" variant="outlined" size="sm" icon="edit" onClick={openDetailsModal}>
                Modifica
              </Button>
            )}
            {!isAdmin(auth) && (
              <RevealSensitiveButton entityType="contracts" entityId={c.id} onRevealed={() => setRevealKey(k => k + 1)} />
            )}
            {/* F-117 (REQ-265-001): stampa e copia link, entrambe tracciate in audit. */}
            <PrintShareButtons entityType="Contract" entityId={c.id} />
            <OverflowMenu aria-label="Altre azioni" items={moreActions} />
          </>
        }
      />

      <Dialog
        open={showTrashForm}
        onClose={() => { if (!trashBusy) { setShowTrashForm(false); setTrashError(null); setTrashDeps(null) } }}
        title="Sposta nel cestino"
        as="form"
        onSubmit={e => void submitTrash(e)}
        dismissible={!trashBusy}
        actions={
          <>
            <Button
              type="button"
              variant="text"
              disabled={trashBusy}
              onClick={() => { setShowTrashForm(false); setTrashError(null); setTrashDeps(null) }}
            >
              Annulla
            </Button>
            <Button
              type="submit"
              variant="text"
              tone="error"
              disabled={trashBusy || (c.status !== 'Bozza' && trashReason.trim() === '')}
            >
              {trashBusy ? 'Spostamento...' : 'Conferma'}
            </Button>
          </>
        }
      >
        {trashError && <div className="md-typescale-body-small text-danger">{trashError}</div>}
        <FormGrid columns={1}>
          <TextField
            id="contract-trash-reason"
            label={c.status === 'Bozza' ? 'Motivo (facoltativo)' : 'Motivo (obbligatorio)'}
            type="text"
            maxLength={500}
            value={trashReason}
            onChange={e => setTrashReason(e.target.value)}
            supportingText="Es. inserita due volte"
          />
        </FormGrid>
        {trashDeps && Object.keys(trashDeps).length > 0 && (
          <ul className="md-typescale-body-small text-danger mb-0 ps-3" data-testid="trash-dependencies">
            {Object.entries(trashDeps).map(([area, n]) => (
              <li key={area}>{area}: {n}</li>
            ))}
          </ul>
        )}
      </Dialog>

      {inTrash && c.deletedAt && (
        <TrashBanner
          kind="contracts"
          id={c.id}
          deletedAt={c.deletedAt}
          reason={c.deleteReason}
          canRestore={isAdmin(auth)}
          onRestored={() => { void loadContract() }}
        />
      )}

      <div className="ecrm-contract">
      <div className="ecrm-contract__grid">

      {/* In cima, a tutta larghezza: dove si trova la pratica e chi deve fare cosa. */}
      {!inTrash && <ContractProgress status={c.status} history={statusHistory} portalPhase={portalPhase} />}

      {/* ═══ Riepilogo fisso: stato, prossimo passo, fatti chiave, note ═══ */}
      <aside className="ecrm-contract__aside" aria-label="Riepilogo del contratto">
        <Card variant="outlined" padding="none" className="ecrm-summary">
          <div className="ecrm-summary__block">
            {/* Lo stato in grande è nella barra "dove si trova la pratica"; qui resta solo nel cestino, dove la barra non c'è. */}
            <span className="ecrm-summary__label">{inTrash ? 'Stato' : 'Incasso e completezza'}</span>
            <div className="ecrm-summary__status">
              {inTrash && statusBadge(c.status)}
              {paymentStatusBadge(c.paymentStatus)}
              {!readinessLoading && readiness && (
                <StatusPill tone={pillTone(CONTRACT_READINESS_COLOR_VARIANTS[readiness.color])}>
                  {readiness.scorePercent}% completa
                </StatusPill>
              )}
            </div>
          </div>

          {/* TASK-0025 / ADR-006: solo le transizioni che il backend dichiara valide dallo stato attuale
              (c.availableStatusTransitions), mai una copia del grafo in TypeScript. */}
          {!inTrash && visibleTransitions.length > 0 && (
            <div className="ecrm-summary__block">
              <span className="ecrm-summary__label">Sposta la pratica a</span>
              <div className="ecrm-summary__actions">
                {visibleTransitions.map((status, i) => {
                  const negative = CONTRACT_STATUS_COLORS[status] === 'danger' || status === 'Annullata'
                  return (
                    <Button
                      key={status}
                      variant={negative ? 'text' : i === 0 ? 'filled' : 'outlined'}
                      {...(negative ? { tone: 'error' as const } : {})}
                      onClick={() => handleStatusChange(status)}
                      disabled={updatingStatus}
                    >
                      {status === 'InviataAlBackoffice' && isExternalAgent(auth) ? 'Invia al backoffice' : (CONTRACT_STATUS_LABELS[status] ?? status)}
                    </Button>
                  )
                })}
              </div>
            </div>
          )}

          <div className="ecrm-summary__block">
            <span className="ecrm-summary__label">Totale previsto</span>
            {/* TASK-0162: totalAmount sostituisce agentVisibleAmount per chi non vede gli importi reali, '—' se manca anche quello. */}
            <div className="ecrm-summary__total">
              {formatEur(totalAmount)}
              {c.paidAmount != null && (
                <small>Incassati {formatEur(c.paidAmount)}{c.paidAt ? ` il ${formatDate(c.paidAt)}` : ''}</small>
              )}
            </div>
          </div>

          <div className="ecrm-summary__block">
            <fieldset disabled={inTrash} className="border-0 p-0 m-0 md-u-minw-0">
            <dl className="ecrm-facts">
              <div>
                <dt>Cliente</dt>
                <dd>
                  {c.customerId != null ? (
                    <Link to={`/customers/${c.customerId}`}>{c.customerName || `Cliente #${c.customerId}`}</Link>
                  ) : (
                    <div className="w-100">
                      <span className="text-muted">Non collegato</span>
                      <Button variant="text" size="sm" disabled={linkingCustomer} onClick={() => { void linkCustomerFromDocs() }}>
                        {linkingCustomer ? 'Collego…' : 'Collega dai dati portale'}
                      </Button>
                      {customerChoices.length > 0 && (
                        <div>
                          {customerLinkNotice ?? 'Più clienti con lo stesso CF/P.IVA, scegli:'}
                          {customerChoices.map(o => (
                            <Button key={o.id} variant="text" size="sm" onClick={() => { void assignCustomer(o.id).catch(e => setError(String(e))) }}>
                              {o.label}
                            </Button>
                          ))}
                        </div>
                      )}
                      <div className="mt-2">
                        <TextField id="contract-customer-search" label="Cerca in anagrafica" type="text" value={customerSearch}
                          onChange={e => setCustomerSearch(e.target.value)} autoComplete="off" disabled={linkingCustomer}
                          supportingText="Nome, ragione sociale, CF, P.IVA o codice cliente." />
                        {customerResults.length > 0 && (
                          <List aria-label="Clienti trovati" className="mt-1">
                            {customerResults.map(c => (
                              <ListItem key={c.id} headline={customerLabel(c)}
                                onClick={() => { void assignCustomer(c.id).catch(e => setError(e instanceof Error ? e.message : String(e))) }} />
                            ))}
                          </List>
                        )}
                        {customerSearch.trim() && customerResults.length === 0 && (
                          <div className="md-typescale-body-small text-muted mt-1">Nessun cliente trovato.</div>
                        )}
                      </div>
                    </div>
                  )}
                </dd>
              </div>
              {/* F-055 (TASK-0309): prodotto e operatore derivato, con "Cambia" in Bozza / Integrazione richiesta. */}
              <div>
                <dt>Prodotto</dt>
                <dd>
                  <ContractProductRow
                    contractId={c.id}
                    status={c.status}
                    productId={c.productId}
                    operatorId={c.operatorId}
                    departmentId={c.departmentId}
                    onChanged={() => { void loadContract() }}
                  />
                </dd>
              </div>
              <div>
                <dt>Data di firma</dt>
                <dd>
                  {c.signedAt ? formatDate(c.signedAt) : <span className="text-muted">Non firmato</span>}
                  <IconButton icon="edit" size="sm" aria-label="Imposta data di firma"
                    onClick={() => setSignedAtDraft((c.signedAt ?? '').slice(0, 10) || new Date().toISOString().slice(0, 10))} />
                </dd>
              </div>
              {/* Codice della pratica sul portale dell'operatore: lo scrive il caricamento automatico; lo staff può impostarlo a mano. */}
              <div>
                <dt>Codice portale</dt>
                <dd>
                  {c.operatorReferenceId || <span className="text-muted">Non ancora caricato</span>}
                  {auth !== null && !isExternalAgent(auth) && (
                    <IconButton icon="edit" size="sm" aria-label="Imposta codice portale" onClick={() => { void editOperatorReferenceId() }} />
                  )}
                </dd>
              </div>
            </dl>
            </fieldset>
          </div>
        </Card>

        {/* Note: nel riepilogo, sempre a portata (su telefono in fondo alla scheda). Nel cestino in sola lettura. */}
        <fieldset disabled={inTrash} className="border-0 p-0 m-0 md-u-minw-0 ecrm-contract__notes">
        <Card variant="outlined" padding="none" className="ecrm-summary">
          <div className="ecrm-summary__block">
            <span className="ecrm-summary__label">Note{notes.length > 0 ? ` · ${notes.length}` : ''}</span>
            {notes.length > 0 && (
              <div className="ecrm-notes mb-3" ref={notesBoxRef}>
                {notes.map(note => (
                  <div key={note.id} className="ecrm-note">
                    <div className="ecrm-note__meta">
                      <span>{note.isInternal ? '🔒 Interna' : 'Nota'}</span>
                      <span>{formatDateTime(note.createdAt)}</span>
                    </div>
                    <div className="ecrm-note__text">{note.content}</div>
                  </div>
                ))}
              </div>
            )}
            <TextField
              label="Scrivi una nota..."
              labelHidden
              placeholder="Scrivi una nota..."
              multiline
              rows={2}
              className="mb-2"
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              disabled={addingNote}
            />
            <div className="d-flex gap-2 align-items-center justify-content-between">
              <Checkbox
                id="noteInternal"
                label="Interna"
                checked={noteInternal}
                onChange={e => setNoteInternal(e.target.checked)}
                disabled={addingNote}
              />
              <Button variant="tonal" size="sm" icon="send" onClick={handleAddNote} disabled={addingNote || !noteText.trim()}>
                {addingNote ? 'Invio...' : 'Aggiungi'}
              </Button>
            </div>
          </div>
        </Card>
        </fieldset>
      </aside>

      {/* ═══ Contenuto: prima ciò che va sistemato, poi le sezioni ═══ */}
      <div className="ecrm-contract__main">

      <div className="ecrm-attention">
      {amountsWarning && (
        <Callout
          tone="warning"
          action={<IconButton icon="close" size="sm" aria-label="Chiudi" onClick={() => setAmountsWarning(null)} />}
        >
          {amountsWarning}
        </Callout>
      )}

      {/* Verifica prima dell'invio — F-074 (TASK-0328) */}
      {submissionReport && (
        <Callout
          tone={submissionReport.kind === 'blocked' ? 'error' : 'warning'}
          data-testid="submission-report"
          action={<IconButton icon="close" size="sm" aria-label="Chiudi" onClick={() => setSubmissionReport(null)} />}
        >
          <strong>{submissionReport.title}</strong>
          {submissionReport.blocking.length > 0 && (
            <ul className="mb-1 mt-2">
              {submissionReport.blocking.map(f => (
                <li key={`b-${f.source}-${f.code}`}>
                  <StatusPill tone="error" className="me-2">Blocca</StatusPill>
                  {f.message}
                </li>
              ))}
            </ul>
          )}
          {submissionReport.warnings.length > 0 && (
            <ul className="mb-0 mt-2">
              {submissionReport.warnings.map(f => (
                <li key={`w-${f.source}-${f.code}`}>
                  <StatusPill tone="warning" className="me-2">Avviso</StatusPill>
                  {f.message}
                </li>
              ))}
            </ul>
          )}
        </Callout>
      )}
      {/* Problema segnalato dal portale (sospesa/KO): motivo, correzione e "Risolto, al reparto". */}
      {!inTrash && <PortalIssuePanel contractId={id} status={c.status} onChanged={() => { void loadContract() }} />}

      {/* Completezza pratica — TASK-0281 (F-027, REQ-035-001/002/003). Placed right under the header,
          full width: the card's own acceptance criterion is "capire in un secondo se è pronta e, se
          no, perché", so it must be the first thing a reader sees, not buried below Note/Attribuzione.
          `readiness.color`/`scorePercent` are rendered EXACTLY as the server computed them — no local
          threshold, no re-derivation — and the colour is never the only signal: the badge always
          carries the Italian label text too (REQ-035-002's own three words), for a reader who cannot
          distinguish red from green. Rendered only once the fetch actually settled (never during
          `readinessLoading`, to avoid a flash of "non disponibile" for what is really just "not
          arrived yet") and only when there is something to show — see the effect above for why a
          genuinely 404 outcome renders nothing here at all. */}
      {!inTrash && !readinessLoading && readinessNeedsAttention && (
        <Card variant="outlined">
          <CardHeader icon="checklist" title="Completezza pratica" />
          <CardContent>
            {readinessUnavailable && (
              <div className="md-typescale-body-small text-muted fst-italic">
                Il punteggio di completezza non è disponibile per questa pratica al momento.
              </div>
            )}

            {contactLinkError && contract?.customerId && !contract.contactId && (
              <div className="md-typescale-body-small mb-2">
                {contactLinkError}{' '}
                <Link to={`/customers/${contract.customerId}`}>Apri il cliente</Link>
                <Button variant="text" size="sm" onClick={() => setContactLinkTried(false)}>Riprova</Button>
              </div>
            )}

            {readiness && (() => {
              const variant = CONTRACT_READINESS_COLOR_VARIANTS[readiness.color]
              const blocking = readiness.unmetRules.filter(r => r.severity === 'Bloccante')
              const warnings = readiness.unmetRules.filter(r => r.severity === 'Avviso')
              // Review fix (P1): a column with content, next to an empty one, is only ever full
              // width when the OTHER column has nothing to show — the real case for all 611
              // production contracts today (2 regole bloccanti, 0 avvisi) is exactly this, and a
              // half-empty card reads as broken/unfinished rather than "nothing to report here".
              const twoCols = blocking.length > 0 && warnings.length > 0
              return (
                <>
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <StatusPill tone={pillTone(variant)}>
                      {readiness.scorePercent}% · {CONTRACT_READINESS_COLOR_LABELS[readiness.color]}
                    </StatusPill>
                  </div>

                  {readiness.unmetRules.length === 0 ? (
                    readiness.color === 'Verde' ? (
                      <div className="md-typescale-body-small text-success">
                        Nessuna mancanza rilevata dalle regole attive.
                      </div>
                    ) : (
                      // Review fix (P1): unmetRules vuoto + colore Giallo significa, per costruzione
                      // di ContractReadinessEvaluator, che il denominatore valutabile è zero — NESSUNA
                      // regola è stata effettivamente applicata a questa pratica (nessuna regola
                      // attiva/applicabile, o tutte NonValutabile). Non è un esito positivo: è
                      // l'assenza di un controllo, lo stesso scenario per cui F-026 impone Giallo
                      // invece di Verde su denominatore zero (fix di revisione su quella card). Un
                      // Rosso con unmetRules vuoto non è raggiungibile dall'algoritmo del backend
                      // (score sarebbe 100), quindi questo ramo copre solo Giallo (e, difensivamente,
                      // qualunque valore diverso da Verde).
                      <div className="md-typescale-body-small text-muted fst-italic">
                        Nessuna regola di validazione è stata effettivamente verificata su questa
                        pratica: il punteggio non riflette un controllo avvenuto.
                      </div>
                    )
                  ) : (
                    <FormGrid columns={twoCols ? 2 : 1}>
                      {blocking.length > 0 && (
                        <div>
                          <div className="md-typescale-emphasized-body-small text-danger mb-1">
                            Bloccanti ({blocking.length})
                          </div>
                          <ul className="mb-0 ps-3 md-typescale-body-small">
                            {blocking.map(r => (
                              <li key={r.ruleCode}>
                                {r.label}
                                {r.status === 'NonValutabile' && r.reason && (
                                  <span className="text-muted"> — {r.reason}</span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {warnings.length > 0 && (
                        <div>
                          {/* bg-warning text needs text-dark for contrast, same as statusBadge's own
                              textCls above — kept as a plain class here since this is a label, not a
                              badge. */}
                          <div className="md-typescale-emphasized-body-small text-warning-emphasis mb-1">
                            Avvisi ({warnings.length})
                          </div>
                          <ul className="mb-0 ps-3 md-typescale-body-small">
                            {warnings.map(r => (
                              <li key={r.ruleCode}>
                                {r.label}
                                {r.status === 'NonValutabile' && r.reason && (
                                  <span className="text-muted"> — {r.reason}</span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </FormGrid>
                  )}
                </>
              )
            })()}
          </CardContent>
        </Card>
      )}
      </div>

      <ContractSection id="dati" title="Dati del contratto" icon="assignment">
      {/* C-08: dopo "Mostra dati completi" la scheda si rimonta e rilegge IBAN e codice fiscale interi. */}
      {!inTrash && <ContractPortalDataCard
        key={revealKey}
        contractId={id}
        operatorId={contract.operatorId ?? null}
        operatorName={operators.find(o => o.id === contract.operatorId)?.name}
        customerSegment={contract.customerSegment}
        // Bozza e Integrazione richiesta: chiunque veda la pratica. Lo staff può correggere i dati anche dopo, finché la
        // pratica non è sul portale dell'operatore (per completarla prima del caricamento).
        readOnly={contract.status !== 'Bozza' && contract.status !== 'IntegrazioneRichiesta'
          && !(auth !== null && !isExternalAgent(auth) && !contract.operatorReferenceId)}
        // Lo staff può caricare sul portale in qualsiasi stato (anche in Bozza), finché non c'è il codice del portale.
        // Dal 26/10/2026 anche un agente esterno, per Ermes, sulle pratiche di cui è l'agente (stesso controllo nell'API);
        // dal 31/10/2026 anche per i portali Coraly dove la card la crea il CRM (Sky, «Carica sul portale»).
        // /api/auth/me dà l'id come testo (valore del claim): si confronta come numero.
        canUploadPortal={auth !== null && (!isExternalAgent(auth) || (contract.agentId === Number(auth.id)
          && (() => { const cfg = operatorPortalConfig(operators.find(o => o.id === contract.operatorId)?.name, contract.operatorId); return !!cfg.ermes || coralyUploadFromCrm(cfg.profile?.portal) })())) && !contract.operatorReferenceId}
        // Bozza Ermes già creata: si possono rimandare sul portale i dati corretti qui, fino alla firma.
        canUpdateErmes={auth !== null && (!isExternalAgent(auth) || contract.agentId === Number(auth.id)) && !!contract.operatorReferenceId}
        reloadKey={syncTick}
        customerId={contract.customerId ?? null}
        status={contract.status}
        portalCode={contract.operatorReferenceId ?? null}
        onSaved={() => { void loadContract(); setReadinessTick(t => t + 1) }}
      />}

      {/* Campi dinamici della pratica — F-074 (TASK-0328) */}
      {!inTrash && <ContractDynamicFields
        key={revealKey}
        contractId={id}
        context={{
          departmentId: contract.departmentId ?? null,
          operatorId: contract.operatorId ?? null,
          productId: contract.productId ?? null,
          customerSegment: contract.customerSegment ?? null,
          contractType: contract.contractType ?? null,
        }}
        readOnly={contract.status !== 'Bozza' && contract.status !== 'IntegrazioneRichiesta'}
      />}
        {/* Attivazione SIM Uno Mobile sul portale 1Mobile, guidata dal CRM */}
        {!inTrash && (contract.operatorId === 5 || contract.operatorId === 17) &&
          <ContractUnoMobileActivation contractId={id} operatorId={contract.operatorId} productId={contract.productId ?? null} />}
        {!inTrash && Number.isFinite(id) && <ContractCoralyPanel contractId={id} onPortalPhase={setPortalPhase}
          // Senza collegamento la scheda serve solo a collegare una pratica Coraly: per gli altri operatori non compare.
          canManage={isBackOfficeOrAdmin(auth) && (operatorPortalConfig(operators.find(o => o.id === contract.operatorId)?.name, contract.operatorId ?? null).profile?.portal ?? '').startsWith('Coraly')} />}
      </ContractSection>

      <ContractSection id="documenti" title="Documenti e firma" icon="folder_open">
        {/* Allegati della pratica con trascinamento — F-141 (REQ-041-001), dietro il flag */}
        {!inTrash && <ContractAttachmentsDrop contractId={id} canDelete={isAdmin(auth)} canEditType={auth !== null && !isExternalAgent(auth)} onChanged={() => setSyncTick(t => t + 1)} />}
        <div className="ecrm-cs__pair">
          {/* Documenti generati dai modelli — F-071 (TASK-0325) */}
          {!inTrash && <ContractGeneratedDocuments contractId={id} />}
          {/* Moduli Ermes da firmare (IVA 10%, accise): compare solo se Ermes o con moduli applicabili */}
          {!inTrash && <ContractErmesModules contractId={id} />}
          {/* R5-02: firma del contratto (flag firma.equal). */}
          {!inTrash && Number.isFinite(id) && <ContractSignaturePanel contractId={id} />}
          {/* C-09: link sicuro per il cliente (flag portale_cliente). */}
          {!inTrash && Number.isFinite(id) && <CustomerLinkPanel contractId={id} />}
        </div>
      {!inTrash && contract.customerId != null && (
        <CustomerSignatureCard
          customerId={contract.customerId}
          required={contract.status === 'Bozza'}
          onSaved={() => setReadinessTick(t => t + 1)}
        />
      )}
      </ContractSection>

      <ContractSection id="economia" title="Importi e provvigioni" icon="euro" defaultOpen={false}>
        <Card variant="outlined">
          <CardContent>
            <KeyValueList columns={2} items={[
              /* TASK-0162: baseAmountDisplay sostituisce agentVisibleAmount per chi non vede gli importi reali. */
              { label: 'Importo base', value: formatEur(baseAmountDisplay) },
              /* TASK-0162: nessun sostituto per l'addon: '—' quando manca. */
              { label: 'Addon', value: formatEur(c.addonTotal) },
              { label: 'Totale previsto', value: <strong>{formatEur(totalAmount)}</strong> },
              { label: 'Incasso', value: <>{paymentStatusBadge(c.paymentStatus)}{c.paidAmount != null && <span className="md-typescale-body-small text-muted">{formatEur(c.paidAmount)}{c.paidAt ? ` il ${formatDate(c.paidAt)}` : ''}</span>}<IconButton icon="edit" size="sm" aria-label="Registra incasso" onClick={openPaymentModal} /></> },
              /* TASK-0204 (REQ-005-003): previsione di vendita, indipendente dallo stato. */
              { label: 'Previsione', value: <>{c.probability != null ? `${c.probability}%` : '—'}{c.expectedCloseDate ? ` · chiusura ${formatDate(c.expectedCloseDate)}` : ''}<IconButton icon="edit" size="sm" aria-label="Registra previsione" onClick={openForecastModal} /></> },
            ]} />
          </CardContent>
        </Card>
        <div className="ecrm-cs__pair">
        {/* IMP-3: movimenti della mandante (pagamento, storno, ricorrente); omessa se non ce ne sono. */}
        {!inTrash && Number.isFinite(id) && <MandantePaymentsPanel contractId={id} />}

        {!inTrash && Number.isFinite(id) && c.baseAmount != null && (
          <ContractOptionsPanel
            contractId={id}
            productId={c.productId}
            operatorId={c.operatorId}
            canManage={isBackOfficeOrAdmin(auth) && c.status !== 'Annullata'}
          />
        )}
        </div>
      {/* Riparto provvigionale (anteprima) — TASK-0267 (F-013, P-21a, REQ-018-004/REQ-058-012/
          REQ-019-001). GET /api/contracts/{id}/commission-preview: DRY-RUN ONLY, nothing here is
          ever written back. Rendered ONLY when the fetch actually returned something or hit a
          genuinely unexpected error — a 403/404 (no CanSeeRealAmounts, Agent-tier, or an invisible
          contract) silently omits the whole card (see the fetch effect above), matching how every
          other permission-gated section on this page defers to the backend's own redaction. A
          missing SignedAt is NOT an error (TASK-0267 review correction): it is a normal
          `commissionPreview` payload whose curated truncation label (below) explains it. */}
      {!inTrash && !commissionPreviewLoading && (commissionPreview || commissionPreviewError) && (
        <Card variant="outlined">
          <CardHeader icon="account_tree" title="Riparto provvigionale (anteprima)" />
          <CardContent>
            {commissionPreviewError && (
              <Callout tone="warning">{commissionPreviewError}</Callout>
            )}

            {commissionPreview && (
              <>
                <FormGrid columns={3} className="mb-3">
                  <div>
                    <div className="md-typescale-body-small text-muted mb-1">Base di calcolo</div>
                    <div className="md-typescale-emphasized-body-medium">{formatEur(commissionPreview.base.amount)}</div>
                    <div className="md-typescale-body-small text-muted">
                      Fonte: {COMMISSION_BASE_SOURCE_LABELS[commissionPreview.base.source] ?? commissionPreview.base.source}
                    </div>
                  </div>
                  <div>
                    <div className="md-typescale-body-small text-muted mb-1">Data di firma / data piano usata</div>
                    <div className="md-typescale-emphasized-body-medium">
                      {commissionPreview.planDate ? formatDate(commissionPreview.planDate) : '—'}
                    </div>
                  </div>
                  {commissionPreview.totals && (
                    <div>
                      <div className="md-typescale-body-small text-muted mb-1">Totale % / residuo azienda</div>
                      <div className="md-typescale-emphasized-body-medium">
                        {formatPct(commissionPreview.totals.totalPercentage)} — {formatEur(commissionPreview.totals.companyResidual)}
                      </div>
                      {commissionPreview.totals.chainTruncated && (
                        <div className="md-typescale-body-small text-danger">Catena troncata</div>
                      )}
                    </div>
                  )}
                </FormGrid>

                {/* TASK-0267 review correction: when the chain's own truncation label below already
                    explains the outcome (e.g. 'signed_at_mancante' -> 'missing_signed_at'), the raw
                    base.note would just repeat the same fact in ungroomed backend prose — skipped
                    here so the user reads ONE curated sentence, not the curated one plus the grezzo
                    one underneath it. */}
                {commissionPreview.base.note && commissionPreview.base.source !== 'signed_at_mancante' && (
                  <Callout tone="info">{commissionPreview.base.note}</Callout>
                )}

                {commissionPreview.chain.truncation !== 'none' && (
                  <Callout tone="warning">
                    {COMMISSION_TRUNCATION_LABELS[commissionPreview.chain.truncation] ?? commissionPreview.chain.truncation}
                  </Callout>
                )}

                {commissionPreview.chain.nodes.length > 0 && (
                  <DataTable variant="plain">
                      <thead>
                        <tr>
                          <th>Livello</th>
                          <th>Beneficiario</th>
                          <th>Ruolo</th>
                          <th>Fonte %</th>
                          <th className="md-num">% lorda</th>
                          <th className="md-num">% netta</th>
                          <th className="md-num">Importo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {commissionPreview.chain.nodes.map(n => (
                          <tr key={`${n.level}-${n.userId}`}>
                            <td>{n.level}</td>
                            <td>{n.userId}</td>
                            <td>
                              {n.commissionRoleId}
                              {n.paysDifferential && (
                                <StatusPill tone="info" className="ms-1">differenziale</StatusPill>
                              )}
                            </td>
                            <td>{COMMISSION_SOURCE_LABELS[n.percentageSource] ?? n.percentageSource}</td>
                            <td className="md-num">{formatPct(n.grossPercentage)}</td>
                            <td className="md-num">{formatPct(n.netPercentage)}</td>
                            <td className="md-num">{n.amount == null ? '—' : formatEur(n.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                  </DataTable>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Storico calcoli — TASK-0268 (F-014, P-21b, REQ-019-002/REQ-016-001). Unlike the anteprima
          card above, every row here was actually PERSISTED: GET /api/contracts/{id}/commission-runs.
          Rendered whenever the fetch resolved (even to an empty list — "nessun calcolo salvato" is a
          normal, informative state, not an error) or hit a genuinely unexpected error; a 403/404 is
          swallowed the same silent way as the preview card above. "Calcola e salva" is shown to
          everyone (the backend, not this client, is the actual Admin boundary — same "defer to the
          server's own redaction/authorization" convention as everywhere else on this page) but
          DISABLED with an explanatory hint for a caller this client already knows is not an Admin, so
          a non-Admin is not left guessing why the button never seems to do anything. */}
      {!inTrash && !commissionRunsLoading && (
        <Card variant="outlined">
          <CardHeader
            icon="history"
            title="Storico calcoli"
            actions={
              <>
                {!isAdmin(auth) && (
                  <span className="md-typescale-body-small text-muted">Solo un Admin può calcolare e salvare</span>
                )}
                <Button
                  variant="filled"
                  onClick={() => void handleCreateCommissionRun()}
                  disabled={creatingRun || !isAdmin(auth)}
                  title={isAdmin(auth) ? undefined : 'Azione riservata agli Admin'}
                >
                  {creatingRun ? 'Calcolo in corso...' : 'Calcola e salva'}
                </Button>
              </>
            }
          />
          <CardContent>
            {commissionRunsError && (
              <Callout tone="warning">{commissionRunsError}</Callout>
            )}
            {createRunError && (
              <Callout tone="error">{createRunError}</Callout>
            )}
            {createRunResult && !createRunResult.computable && (
              <Callout tone="info">{createRunResult.note}</Callout>
            )}
            {createRunResult?.computable && createRunResult.unchanged && (
              <Callout tone="info">
                Nessuna modifica rispetto all'ultimo calcolo salvato (run #{createRunResult.runId}):
                l'impronta degli input coincide, nessuna riga nuova è stata creata.
              </Callout>
            )}
            {createRunResult?.computable && !createRunResult.unchanged && (
              <Callout tone="success">
                Nuovo calcolo salvato (run #{createRunResult.runId}):{' '}
                {formatPct(createRunResult.totalPercentage)} — {formatEur(createRunResult.companyResidual)} residuo azienda.
              </Callout>
            )}

            {commissionRuns.length === 0 ? (
              <div className="text-muted md-typescale-body-small mb-0">Nessun calcolo salvato per questo contratto.</div>
            ) : (
              <DataTable variant="plain">
                  <thead>
                    <tr>
                      <th>Data calcolo</th>
                      <th>Data piano</th>
                      <th>Base</th>
                      <th>Fonte base</th>
                      <th className="md-num">Totale %</th>
                      <th className="md-num">Totale importo</th>
                      <th className="md-num">Residuo azienda</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {commissionRuns.map(run => (
                      <Fragment key={run.id}>
                        <tr>
                          <td>{formatDateTime(run.runAt)}</td>
                          <td>{formatDate(run.planDate)}</td>
                          <td>{formatEur(run.basisAmount)}</td>
                          <td>{COMMISSION_BASE_SOURCE_LABELS[run.basisSource] ?? run.basisSource}</td>
                          <td className="md-num">{formatPct(run.totalPercentage)}</td>
                          <td className="md-num">{formatEur(run.totalAmount)}</td>
                          <td className="md-num">{formatEur(run.companyResidual)}</td>
                          <td className="md-num">
                            <Button
                              variant="outlined"
                              onClick={() => toggleRunDetail(run.id)}
                            >
                              {expandedRunId === run.id ? 'Nascondi' : 'Perché questo importo'}
                            </Button>
                          </td>
                        </tr>
                        {expandedRunId === run.id && (
                          <tr>
                            <td colSpan={8}>
                              {runDetailLoading && (
                                <div className="md-typescale-body-small text-muted py-2">Caricamento dettaglio...</div>
                              )}
                              {runDetailError && (
                                <Callout tone="warning">{runDetailError}</Callout>
                              )}
                              {runDetail && !runDetailLoading && (
                                <DataTable variant="plain">
                                    <thead>
                                      <tr>
                                        <th>Livello</th>
                                        <th>Beneficiario</th>
                                        <th>Ruolo</th>
                                        <th>Fonte %</th>
                                        <th className="md-num">% lorda</th>
                                        <th className="md-num">% netta</th>
                                        <th className="md-num">Importo</th>
                                        <th>Corrente</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {runDetail.lines.map(line => (
                                        <tr key={`${line.level}-${line.beneficiaryUserId}`}>
                                          <td>{line.level}</td>
                                          <td>{line.beneficiaryUserId}</td>
                                          <td>{line.roleName ?? line.commissionRoleId}</td>
                                          <td>{COMMISSION_SOURCE_LABELS[line.percentageSource] ?? line.percentageSource}</td>
                                          <td className="md-num">{formatPct(line.grossPercentage)}</td>
                                          <td className="md-num">{formatPct(line.appliedPercentage)}</td>
                                          <td className="md-num">{line.amount == null ? '—' : formatEur(line.amount)}</td>
                                          <td>{line.isCurrent ? 'Sì' : 'Storico'}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                </DataTable>
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
              </DataTable>
            )}
          </CardContent>
        </Card>
      )}
      </ContractSection>

      <ContractSection id="persone" title="Lavorazione e provenienza" icon="groups" defaultOpen={false}>
        {/* F-089 (TASK-0343): priorità con la sua origine (a mano / regola), stato SLA ed eccezioni. Le mancanze sono già in
            "Completezza pratica" in cima: qui resta come dato di lavorazione. */}
        {!inTrash && <ContractQueueStatus contractId={id} />}
        <div className="ecrm-cs__pair">
          {/* Attribuzione — TASK-0274 (F-020, REQ-083-001). Five roles together: Lead Owner/Contract
              Owner are READ-ONLY (they come from elsewhere — Lead.AssignedAgentId/Contract.AgentId,
              shown here only, never duplicated as new columns); the other three are edited through
              the modal below. */}
          <Card variant="outlined">
            <CardHeader
              icon="groups"
              title="Attribuzione"
              actions={
                isBackOfficeOrAdmin(auth) && attribution ? (
                  <IconButton
                    icon="edit"
                    size="sm"
                    aria-label="Modifica attribuzione"
                    onClick={openAttributionModal}
                  />
                ) : undefined
              }
            />
            <CardContent>
              {attributionLoading ? (
                <div className="md-typescale-body-small text-muted fst-italic">Caricamento...</div>
              ) : attributionError ? (
                <div className="md-typescale-body-small text-danger">{attributionError}</div>
              ) : attribution ? (
                <KeyValueList items={[
                  { label: 'Lead Owner', value: userLabel(attribution.leadOwnerUserId) },
                  { label: 'Contract Owner', value: userLabel(attribution.contractOwnerUserId) },
                  { label: 'Lead Generator', value: userLabel(attribution.leadGeneratorUserId) },
                  { label: 'Appointment Setter', value: userLabel(attribution.appointmentSetterUserId) },
                  { label: 'Sales Closer', value: userLabel(attribution.salesCloserUserId) },
                ]} />
              ) : (
                <div className="md-typescale-body-small text-muted fst-italic">Attribuzione non disponibile.</div>
              )}
            </CardContent>
          </Card>
        {/* Provenienza: il lead da cui nasce la pratica — F-110 (TASK-0364) */}
        {!inTrash && <ContractLeadOrigin contractId={id} />}
        </div>
        {/* Dati raccolti dal call center (risposte dei lead convertiti in questa pratica) — F-088 (TASK-0342) */}
        {!inTrash && <ContractCallCenterData contractId={id} />}
      </ContractSection>

      <ContractSection id="cronologia" title="Cronologia" icon="history" hint={`Caricato il ${formatDate(c.createdAt)}${c.updatedAt ? ` · aggiornato il ${formatDate(c.updatedAt)}` : ''}`} defaultOpen={false}>
        {(c.contractType || (c.referenceCode && isTechnicalReference(c.referenceCode))) && (
          <KeyValueList columns={2} items={[
            ...(c.contractType ? [{ label: 'Tipo', value: c.contractType }] : []),
            ...(c.referenceCode && isTechnicalReference(c.referenceCode) ? [{ label: 'Codice interno', value: c.referenceCode }] : []),
          ]} />
        )}
        {/* Cronologia unificata — F-058 (TASK-0312) */}
        <ContractTimeline contractId={id} />
      {/* Storico stato — TASK-0025 / ADR-006 */}
      <Card variant="outlined" padding="none">
        <CardHeader
          icon="history"
          title="Storico stato"
          actions={<StatusPill tone="neutral">{statusHistory.length}</StatusPill>}
        />
        <CardContent className="p-0">
          {statusHistory.length === 0 ? (
            <EmptyState icon="history" title="Nessuna transizione registrata" compact />
          ) : (
            <DataTable variant="plain">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Da</th>
                    <th>A</th>
                    <th>Utente</th>
                    <th>Motivazione</th>
                  </tr>
                </thead>
                <tbody>
                  {statusHistory.map(h => (
                    <tr key={h.id}>
                      <td className="text-nowrap">{formatDateTime(h.occurredAt)}</td>
                      <td>{h.fromStatus ? (CONTRACT_STATUS_LABELS[h.fromStatus] ?? h.fromStatus) : '—'}</td>
                      <td>{CONTRACT_STATUS_LABELS[h.toStatus] ?? h.toStatus}</td>
                      <td>{h.actorUserId ?? (h.origin === 'Automation' ? 'Sistema' : '—')}</td>
                      <td>{h.reason ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
            </DataTable>
          )}
        </CardContent>
      </Card>
        {/* Configurazione usata (riferimenti versionati alla creazione e all'invio) — F-081 (TASK-0335) */}
        {!inTrash && <ContractConfigurationSnapshots contractId={id} />}
      </ContractSection>

      </div>
      </div>
      </div>

      {/* Registra incasso — TASK-0082. Finestra di dialogo del design system (Dialog). */}
      <Dialog
        open={signedAtDraft !== null}
        onClose={() => setSignedAtDraft(null)}
        title="Data di firma"
        as="form"
        onSubmit={e => { e.preventDefault(); void saveSignedAt() }}
        actions={
          <>
            <Button type="button" variant="text" onClick={() => setSignedAtDraft(null)} disabled={savingSignedAt}>
              Annulla
            </Button>
            <Button type="submit" variant="text" disabled={savingSignedAt}>
              {savingSignedAt ? 'Salvataggio...' : 'Salva'}
            </Button>
          </>
        }
      >
        <FormGrid>
          <TextField id="contract-signed-at" label="Data di firma" type="date" value={signedAtDraft ?? ''} onChange={e => setSignedAtDraft(e.target.value)} />
        </FormGrid>
      </Dialog>
      <Dialog
        open={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        title="Registra incasso"
        as="form"
        onSubmit={e => { e.preventDefault(); void handleSavePayment() }}
        actions={
          <>
            <Button type="button" variant="text" onClick={() => setShowPaymentModal(false)} disabled={savingPayment}>
              Annulla
            </Button>
            <Button type="submit" variant="text" disabled={savingPayment}>
              {savingPayment ? 'Salvataggio...' : 'Salva'}
            </Button>
          </>
        }
      >
        <FormGrid>
          <Select
            id="paymentStatus"
            label="Stato pagamento"
            className="md-span-full"
            value={paymentForm.paymentStatus}
            onChange={e => setPaymentForm(f => ({ ...f, paymentStatus: e.target.value }))}
            disabled={savingPayment}
          >
            {/* Exactly the three values ContractsPage.tsx's "Incasso" filter queries —
                shared from api/contracts.ts so the two can never drift apart again. */}
            {PAYMENT_STATUS_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </Select>
          <TextField
            id="paidAmount"
            label="Importo incassato"
            type="number"
            step="0.01"
            min="0"
            value={paymentForm.paidAmount}
            onChange={e => setPaymentForm(f => ({ ...f, paidAmount: e.target.value }))}
            disabled={savingPayment}
          />
          <TextField
            id="paidAt"
            label="Data incasso"
            type="date"
            value={paymentForm.paidAt}
            onChange={e => setPaymentForm(f => ({ ...f, paidAt: e.target.value }))}
            disabled={savingPayment}
          />
        </FormGrid>
      </Dialog>

      <Dialog
        open={amountsForm !== null}
        onClose={() => setAmountsForm(null)}
        title="Modifica importi"
        as="form"
        onSubmit={e => { e.preventDefault(); void handleSaveAmounts() }}
        actions={
          <>
            <Button type="button" variant="text" onClick={() => setAmountsForm(null)} disabled={savingAmounts}>
              Annulla
            </Button>
            <Button type="submit" variant="text" disabled={savingAmounts}>
              {savingAmounts ? 'Salvataggio...' : 'Salva'}
            </Button>
          </>
        }
      >
        <FormGrid>
          <TextField
            id="amountsBase"
            label="Importo base"
            type="number"
            step="0.01"
            min="0"
            value={amountsForm?.baseAmount ?? ''}
            onChange={e => setAmountsForm(f => f && ({ ...f, baseAmount: e.target.value }))}
            disabled={savingAmounts}
          />
          <TextField
            id="amountsAddon"
            label="Addon"
            type="number"
            step="0.01"
            min="0"
            value={amountsForm?.addonTotal ?? ''}
            onChange={e => setAmountsForm(f => f && ({ ...f, addonTotal: e.target.value }))}
            disabled={savingAmounts}
          />
        </FormGrid>
      </Dialog>

      <Dialog
        open={detailsForm !== null}
        onClose={() => setDetailsForm(null)}
        title="Modifica pratica"
        as="form"
        onSubmit={e => { e.preventDefault(); void handleSaveDetails() }}
        actions={
          <>
            <Button type="button" variant="text" onClick={() => setDetailsForm(null)} disabled={savingDetails}>
              Annulla
            </Button>
            <Button type="submit" variant="text" disabled={savingDetails}>
              {savingDetails ? 'Salvataggio...' : 'Salva'}
            </Button>
          </>
        }
      >
        {detailsError && <Callout tone="error">{detailsError}</Callout>}
        <FormGrid>
          <TextField
            id="detailsContractType"
            label="Tipo contratto"
            maxLength={50}
            value={detailsForm?.contractType ?? ''}
            onChange={e => setDetailsForm(f => f && ({ ...f, contractType: e.target.value }))}
            disabled={savingDetails}
          />
          <Select
            id="detailsSegment"
            label="Cliente"
            value={detailsForm?.customerSegment ?? ''}
            onChange={e => setDetailsForm(f => f && ({ ...f, customerSegment: e.target.value }))}
            disabled={savingDetails}
          >
            <option value="">— Non indicato —</option>
            {CUSTOMER_SEGMENT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
          <Select
            id="detailsDepartment"
            label="Reparto"
            value={detailsForm?.departmentId ?? ''}
            onChange={e => setDetailsForm(f => f && ({ ...f, departmentId: e.target.value }))}
            disabled={savingDetails}
          >
            <option value="">— Nessuno —</option>
            {departments.filter(d => d.isActive || String(d.id) === detailsForm?.departmentId).map(d => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </Select>
          <Select
            id="detailsOperator"
            label="Operatore"
            value={detailsForm?.operatorId ?? ''}
            // Cambiando operatore il prodotto va riscelto tra i suoi.
            onChange={e => setDetailsForm(f => f && ({
              ...f, operatorId: e.target.value,
              productId: e.target.value === String(contract?.operatorId ?? '') ? String(contract?.productId ?? '') : '',
            }))}
            disabled={savingDetails}
          >
            <option value="">— Tutti —</option>
            {operators.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </Select>
          <Select
            id="detailsProduct"
            label="Prodotto"
            value={detailsForm?.productId ?? ''}
            onChange={e => setDetailsForm(f => f && ({ ...f, productId: e.target.value }))}
            disabled={savingDetails}
          >
            <option value="">{contract?.productId != null ? '— Invariato —' : '— Scegli il prodotto —'}</option>
            {detailsForm?.productId && !detailsProducts.some(p => String(p.id) === detailsForm.productId) && (
              <option value={detailsForm.productId}>Prodotto attuale (#{detailsForm.productId})</option>
            )}
            {detailsProducts.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
          <Select
            id="detailsMandante"
            label="Mandante"
            value={detailsForm?.mandanteId ?? ''}
            onChange={e => setDetailsForm(f => f && ({ ...f, mandanteId: e.target.value }))}
            disabled={savingDetails}
          >
            <option value="">— Nessuno —</option>
            {mandanti.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </Select>
        </FormGrid>
        <div className="md-typescale-body-small">
          Agente, cliente, importi e dati del portale si modificano dalle rispettive sezioni della pratica.
        </div>
      </Dialog>

      {/* Registra previsione — TASK-0204 (REQ-005-003). Stessa forma del dialogo "Registra incasso". */}
      <Dialog
        open={showForecastModal}
        onClose={() => setShowForecastModal(false)}
        title="Registra previsione"
        as="form"
        onSubmit={e => { e.preventDefault(); void handleSaveForecast() }}
        actions={
          <>
            <Button type="button" variant="text" onClick={() => setShowForecastModal(false)} disabled={savingForecast}>
              Annulla
            </Button>
            <Button type="submit" variant="text" disabled={savingForecast}>
              {savingForecast ? 'Salvataggio...' : 'Salva'}
            </Button>
          </>
        }
      >
        {forecastError && <Callout tone="error">{forecastError}</Callout>}
        <FormGrid>
          <TextField
            id="probability"
            label="Probabilità (%)"
            type="number"
            step="1"
            min="0"
            max="100"
            value={forecastForm.probability}
            onChange={e => setForecastForm(f => ({ ...f, probability: e.target.value }))}
            disabled={savingForecast}
          />
          <TextField
            id="expectedCloseDate"
            label="Chiusura prevista"
            type="date"
            value={forecastForm.expectedCloseDate}
            onChange={e => setForecastForm(f => ({ ...f, expectedCloseDate: e.target.value }))}
            disabled={savingForecast}
          />
        </FormGrid>
      </Dialog>

      {/* Attribuzione — TASK-0274 (F-020, REQ-083-001). Stessa forma dei dialoghi precedenti.
          Edits and resubmits all three editable roles together — see handleSaveAttribution's own remarks. */}
      <Dialog
        open={showAttributionModal}
        onClose={() => setShowAttributionModal(false)}
        title="Attribuzione"
        as="form"
        onSubmit={e => { e.preventDefault(); void handleSaveAttribution() }}
        actions={
          <>
            <Button type="button" variant="text" onClick={() => setShowAttributionModal(false)} disabled={savingAttribution}>
              Annulla
            </Button>
            <Button type="submit" variant="text" disabled={savingAttribution}>
              {savingAttribution ? 'Salvataggio...' : 'Salva'}
            </Button>
          </>
        }
      >
        {attributionSaveError && (
          <Callout tone="error">{attributionSaveError}</Callout>
        )}
        {usersError && (
          <Callout tone="warning">
            Impossibile caricare l'elenco utenti (permessi insufficienti per il tuo ruolo):
            inserisci l'ID utente direttamente.
          </Callout>
        )}
        <FormGrid columns={1}>
          {renderUserPicker(
            'contractOwnerUserId', 'Agente di riferimento (Contract Owner)',
            attributionForm.contractOwnerUserId,
            v => setAttributionForm(f => ({ ...f, contractOwnerUserId: v })),
          )}
          {renderUserPicker(
            'leadGeneratorUserId', 'Lead Generator',
            attributionForm.leadGeneratorUserId,
            v => setAttributionForm(f => ({ ...f, leadGeneratorUserId: v })),
          )}
          {renderUserPicker(
            'appointmentSetterUserId', 'Appointment Setter',
            attributionForm.appointmentSetterUserId,
            v => setAttributionForm(f => ({ ...f, appointmentSetterUserId: v })),
          )}
          {renderUserPicker(
            'salesCloserUserId', 'Sales Closer',
            attributionForm.salesCloserUserId,
            v => setAttributionForm(f => ({ ...f, salesCloserUserId: v })),
          )}
        </FormGrid>
      </Dialog>
    </Page>
  )
}

/**
 * F-141 (REQ-041-001): allegati della pratica con zona di trascinamento. Carica con lo stesso `POST /api/documents` della
 * pagina Documenti (EntityType "Contract"), quindi stessi limiti e stessi controlli di visibilita' del server; un file
 * ancora "in coda" (caricamento asincrono sullo storage) si vede subito con la sua etichetta.
 * Dal 27/10/2026 sempre visibile: stava dietro il flag `contratti.salvataggio_automatico` e chi non lo aveva (gli
 * agenti) non vedeva nessun allegato della propria pratica, né «Prendi i documenti del cliente».
 */
function ContractAttachmentsDrop({ contractId, canDelete = false, canEditType = false, onChanged }: { contractId: number; canDelete?: boolean; canEditType?: boolean; onChanged?: () => void }) {
  const [items, setItems] = useState<DocumentSummary[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const confirm = useConfirm()
  // Tipo scelto per i prossimi file: decide la casella sui portali (Ermes: bolletta / documento di identità / aggiuntivi).
  const [uploadType, setUploadType] = useState('')
  const [copyMsg, setCopyMsg] = useState<string | null>(null)

  async function copyFromCustomer(): Promise<void> {
    setBusy(true)
    setError(null)
    setCopyMsg(null)
    try {
      const r = await contractsApi.copyCustomerDocuments(contractId)
      setCopyMsg(r.copiati.length > 0
        ? `Presi ${r.copiati.length} documenti: ${r.copiati.join(', ')}.`
        : `Il cliente non ha altri documenti da prendere${r.dettaglio ? ` (${r.dettaglio})` : ''}.`)
      setItems(await documentsApi.list('Contract', contractId))
      onChanged?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  async function changeType(d: DocumentSummary, type: string): Promise<void> {
    setBusy(true)
    setError(null)
    try {
      await documentsApi.setType(d.id, type || null)
      await reload()
      onChanged?.()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Cambio del tipo non riuscito')
    } finally {
      setBusy(false)
    }
  }

  // Solo amministratori (DELETE /api/documents/{id}): un file sbagliato non deve finire sul portale dell'operatore.
  async function remove(d: DocumentSummary): Promise<void> {
    if (!await confirm({ title: 'Eliminare l\'allegato?', message: `"${d.name}" viene tolto dalla pratica e non sarà più caricato sui portali. Se è già sul portale, va tolto anche lì.`, confirmLabel: 'Elimina', destructive: true })) return
    setBusy(true)
    setError(null)
    try {
      await documentsApi.remove(d.id)
      await reload()
      onChanged?.()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Eliminazione non riuscita')
    } finally {
      setBusy(false)
    }
  }

  const reload = useCallback(async () => {
    try {
      setItems(await documentsApi.list('Contract', contractId))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nel caricamento degli allegati')
    }
  }, [contractId])

  useEffect(() => {
    void reload()
  }, [reload])

  async function onFiles(files: File[]): Promise<void> {
    setBusy(true)
    setError(null)
    try {
      for (const file of files) await documentsApi.upload('Contract', contractId, file, undefined, undefined, uploadType || undefined)
      await reload()
      onChanged?.()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore durante il caricamento')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card variant="outlined" className="mb-3" id="contract-allegati">
      <CardHeader title="Allegati" subtitle="Il tipo decide la casella sui portali (Ermes: bolletta, documento di identità, documenti aggiuntivi)" />
      <CardContent>
        {error && <Callout tone="error">{error}</Callout>}
        <div className="mb-2">
          <Select id="contract-allegati-tipo" label="Tipo dei file da caricare" value={uploadType} onChange={e => setUploadType(e.target.value)}>
            {CONTRACT_DOCUMENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </Select>
        </div>
        <FileDropZone onFiles={onFiles} busy={busy} label="Trascina qui i documenti della pratica (o clicca per sceglierli)" />
        <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
          {/* Cliente già in anagrafica: carta d'identità e visura dalla scheda cliente o dall'ultima pratica, senza ricaricarli. */}
          <Button variant="text" size="sm" disabled={busy} onClick={() => { void copyFromCustomer() }}>Prendi i documenti del cliente</Button>
          {copyMsg && <span className="md-typescale-body-small text-muted">{copyMsg}</span>}
        </div>
        {items.length > 0 && (
          // La scheda sta in una colonna stretta: nome del file su una riga (spezzato dove serve), tipo e comandi sotto.
          <ul aria-label="Allegati della pratica" className="list-unstyled m-0 mt-2">
            {items.map(d => (
              <li key={d.id} className="py-2 border-top">
                {/* Nome = apre il file in una nuova scheda (anteprima inline per PDF e immagini): sul telefono, soprattutto
                    con l'app sulla schermata Home, un link con il solo attributo download spesso non fa nulla. */}
                <a href={documentsApi.previewUrl(d.id)} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{d.name}</a>
                <div className="d-flex flex-wrap align-items-center gap-2 mt-1">
                  <a href={documentsApi.contentUrl(d.id)} download={d.name} target="_blank" rel="noopener noreferrer" className="md-typescale-body-small">Scarica</a>
                  {canEditType ? (
                    <Select id={`contract-allegato-tipo-${d.id}`} label="Tipo" value={CONTRACT_DOCUMENT_TYPES.some(t => t.value === (d.documentType ?? '')) ? (d.documentType ?? '') : 'altro'}
                      disabled={busy} onChange={e => { void changeType(d, e.target.value) }}>
                      {CONTRACT_DOCUMENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </Select>
                  ) : (
                    <span className="md-typescale-body-small text-muted">Tipo: {contractDocumentTypeLabel(d.documentType)}</span>
                  )}
                  {d.uploadStatus === 'InCoda' && <StatusPill tone="info">In coda</StatusPill>}
                  {d.uploadStatus === 'Errore' && <StatusPill tone="error">Errore di caricamento</StatusPill>}
                  {canDelete && (
                    <Button variant="text" size="sm" tone="error" disabled={busy} onClick={() => void remove(d)}>Elimina</Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}
